import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { RotateCcw, Loader2, Share2, Printer, FileDown, Users, Check, X, Globe, Video, Film, Download, Mic, Music, Upload, Play, Square, Captions, ZoomIn, Images, Info, Wifi, Smartphone, BatteryCharging, RectangleHorizontal, RectangleVertical, ShoppingCart, Sparkles, Timer, ScanSearch, ShieldCheck, AlertTriangle, EyeOff, Wand2, Link2, FlipHorizontal, MonitorPlay, ClipboardList, KeyRound, ExternalLink, Pencil, MapPin, Copy, Type, Volume2, ChevronLeft, ChevronRight, LayoutGrid, ListChecks, MoveHorizontal, VolumeX, RefreshCw, MessageCircle, Plus, ChevronsDownUp, ChevronsUpDown, Languages } from "lucide-react";
import VoiceStudioModal from "@/components/VoiceStudioModal";
import api, { imageUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import RetentionCardStudio from "@/components/RetentionCardStudio";
import RiddleCardStudio from "@/components/RiddleCardStudio";
import EndCardStudio from "@/components/EndCardStudio";
import VideoQuizStudio from "@/components/VideoQuizStudio";
import IntroClipStudio from "@/components/IntroClipStudio";
import StepClipEditor from "@/components/StepClipEditor";
import CaptionEditModal from "@/components/CaptionEditModal";
import PrintableManual from "@/components/PrintableManual";
import ThumbCropModal from "@/components/ThumbCropModal";
import StaircaseLoader from "@/components/StaircaseLoader";
import Logo from "@/components/Logo";
import { RenderStopwatches } from "@/components/RenderStopwatches";
import { SpendTicker } from "@/components/SpendTicker";
import { shareManual, printManual, savePdf, saveServerPdf, saveAmazonLinksPdf } from "@/lib/share";
import { saveOrShareVideo } from "@/lib/saveVideo";
import { playDoneJingle } from "@/lib/jingle";
import { playBell } from "@/lib/bell";
import { toast } from "sonner";

const COMMUNITY_CATS = ["Lifestyle", "Education (Kids)", "Education (Teens)", "Education (Adults)", "Teachers", "Government", "Current News", "Philosophy", "Religion", "History", "Food", "Tech", "Home", "People", "Fashion", "Animals", "Games", "Jobs", "Music", "Automotive", "Self Help", "Collectibles", "Hobbies", "Weather", "Art"];

// Real display faces for the free (PIL-composited) Thumbnail Studio — `key` maps to a server font file.
const THUMB_FONTS = [
  { key: "anton", label: "Bold Impact" },
  { key: "bebas", label: "Tall Condensed" },
  { key: "oswald", label: "Condensed Sans" },
  { key: "montserrat", label: "Clean Geometric" },
  { key: "poppins", label: "Modern Rounded" },
  { key: "playfair", label: "Elegant Serif" },
  { key: "pacifico", label: "Handwritten Script" },
  { key: "righteous", label: "Retro Tech" },
  { key: "audiowide", label: "Futuristic Tech" },
  { key: "teko", label: "Sport Condensed" },
  { key: "cinzel", label: "Classic Roman" },
  { key: "rakkas", label: "Arabic Flair" },
  { key: "lalezar", label: "Persian Display" },
  { key: "lobster", label: "Retro Script" },
  { key: "bungee", label: "Urban Display" },
  { key: "alfaslab", label: "Heavy Slab" },
  { key: "passion", label: "Bold Poster" },
];

// Where the title sits on the chosen photo.
const THUMB_PLACEMENTS = [
  { key: "top", label: "Top" },
  { key: "top-left", label: "Top-Left" },
  { key: "middle", label: "Middle" },
  { key: "bottom", label: "Bottom" },
  { key: "bottom-left", label: "Bottom-Left" },
  { key: "left", label: "Left" },
  { key: "right", label: "Right" },
];

// Approximate browser fonts + sizes for the placement preview.

// ── Gallery image loader with a global concurrency gate ───────────────────────
// Loading all 15 step thumbnails at once hammers the backend/GridFS and some stall.
// We cap in-flight image requests to LOAD_BATCH (5) at a time; the rest queue and
// start as soon as a slot frees up. Each thumb still self-heals transient 404s
// (a freshly-rendered photo's URL can 404 for a beat) by retrying with a cache-buster.
const LOAD_BATCH = 5;
let _imgActive = 0;
const _imgWaiters = [];
function acquireImgSlot() {
  return new Promise((resolve) => {
    if (_imgActive < LOAD_BATCH) { _imgActive++; resolve(); }
    else _imgWaiters.push(resolve);
  });
}
function releaseImgSlot() {
  const next = _imgWaiters.shift();
  if (next) next();               // hand the slot straight to the next waiter
  else _imgActive = Math.max(0, _imgActive - 1);
}

function StepThumbImg({ src, alt, busy }) {
  const [status, setStatus] = useState("loading"); // loading | ok
  const [tries, setTries] = useState(0);
  const [ready, setReady] = useState(false);        // acquired a load slot?
  const holding = useRef(false);                    // currently occupying a slot?
  const timer = useRef(null);

  const release = () => { if (holding.current) { holding.current = false; releaseImgSlot(); } };

  // Acquire a slot (respecting the 5-at-a-time cap) before we let the <img> request.
  useEffect(() => {
    let cancelled = false;
    setStatus("loading"); setTries(0); setReady(false);
    acquireImgSlot().then(() => {
      if (cancelled) { releaseImgSlot(); return; }
      holding.current = true;
      setReady(true);
    });
    return () => {
      cancelled = true;
      if (timer.current) clearTimeout(timer.current);
      release();
    };
  }, [src]);

  const finalSrc = tries > 0 ? `${src}${src.includes("?") ? "&" : "?"}r=${tries}` : src;

  return (
    <>
      {status !== "ok" && (
        <div className="absolute inset-0 flex items-center justify-center text-[#b3aec6] bg-[#f1eff8]">
          <Loader2 size={16} className="spin" />
        </div>
      )}
      {ready && (
        <img
          src={finalSrc}
          alt={alt}
          onLoad={() => { setStatus("ok"); release(); }}
          onError={() => {
            release();                       // free the slot so others can load while we back off
            if (tries < 15) {
              timer.current = setTimeout(() => {
                acquireImgSlot().then(() => { holding.current = true; setTries((t) => t + 1); });
              }, Math.min(3000, 700 * (tries + 1)));
            }
          }}
          className={`w-full h-full object-cover transition-opacity duration-300 ${status === "ok" ? (busy ? "opacity-40" : "opacity-100") : "opacity-0"}`}
        />
      )}
    </>
  );
}


// Solid title colors (value is the exact hex sent to the server overlay).
const THUMB_COLORS = [
  { label: "White", value: "#ffffff" },
  { label: "Black", value: "#111111" },
  { label: "Yellow", value: "#ffd21e" },
  { label: "Red", value: "#e11d2a" },
  { label: "Blue", value: "#1e6fff" },
  { label: "Green", value: "#22c55e" },
  { label: "Pink", value: "#ff2d9b" },
  { label: "Orange", value: "#ff7a1a" },
  { label: "Purple", value: "#8b2fd6" },
  { label: "Gold", value: "#d4af37" },
  { label: "Grey", value: "#6b7280" },
  { label: "Brown", value: "#6b4a2b" },
  { label: "Slate", value: "#475569" },
  { label: "Teal", value: "#0d9488" },
];

// Gradient title fills — rendered server-side onto the letters (title only, not outlines).
const THUMB_GRADIENTS = [
  { label: "Chrome", value: "grad:chrome", css: "linear-gradient(180deg,#6eaadc 0%,#cfe0f0 48%,#7a5f3c 56%,#5a4126 100%)" },
  { label: "Sunset", value: "grad:sunset", css: "linear-gradient(180deg,#ffaa1e 0%,#ff2d9b 55%,#8b2fd6 100%)" },
];


// Friendly labels for the auto-matched, culture-inspired music beds.
const fmtElapsed = (s) => {
  if (s == null || isNaN(s)) return "—";
  const m = Math.floor(s / 60);
  const sec = Math.round(s % 60);
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
};

// A render entry is {t: seconds, steps: N}; older entries were a bare number.
const secsOf = (e) => (typeof e === "number" ? e : e?.t);

// Rotating words of comfort shown while the video renders (replaces the raw countdown).
const COMFORT_LINES = [
  "Almost done — hang in there",
  "Go grab yourself something to drink, I'll be done soon",
  "Patience is a virtue",
  "Good things come to those who wait",
  "Processing…",
  "This video is going to be great",
  "Worth the wait, I promise",
  "Putting it all together for you",
  "Great things take a moment",
  "Sit back and relax — I've got this",
  "How do you make a person impatient?",
  "I'll tell you later!",
];

// Aspect-ratio metadata shared by the picker and the "create the other one" prompt.
const ORIENT = {
  landscape: { label: "16:9", name: "YouTube · TV" },
  portrait: { label: "9:16", name: "Reels · Shorts" },
};

// Latin-script languages the PDF can be downloaded in (client PDF font can't render CJK).
const PDF_LANGS = [
  { code: "en", label: "EN" },
  { code: "fr", label: "FR" },
  { code: "it", label: "IT" },
  { code: "es", label: "ES" },
  { code: "de", label: "DE" },
  { code: "pt", label: "PT" },
  { code: "zh", label: "中文" },
  { code: "ja", label: "日本語" },
];
// Non-Latin scripts are rendered server-side (browser PDF font can't draw CJK).
const SERVER_PDF_LANGS = new Set(["zh", "ja"]);

const LANGS = {
  en: { label: "English", native: "English" },
  es: { label: "Spanish", native: "Español" },
  fr: { label: "French", native: "Français" },
  de: { label: "German", native: "Deutsch" },
  it: { label: "Italian", native: "Italiano" },
  zh: { label: "Chinese", native: "中文" },
  ja: { label: "Japanese", native: "日本語" },
  pt: { label: "Portuguese", native: "Português" },
};
const LANG_ORDER = ["en", "es", "fr", "de", "it", "zh", "ja", "pt"];
const parseVariantKey = (k) => {
  const [lang, orient] = k.includes(":") ? k.split(":") : ["en", k];
  return { lang, orient };
};
const sortVariants = (a, b) => {
  const A = parseVariantKey(a), B = parseVariantKey(b);
  const li = LANG_ORDER.indexOf(A.lang) - LANG_ORDER.indexOf(B.lang);
  if (li !== 0) return li;
  return (A.orient === "portrait" ? 0 : 1) - (B.orient === "portrait" ? 0 : 1);
};

export default function Completion() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { refresh, user } = useAuth();
  const [manual, setManual] = useState(null);
  const [videoState, setVideoState] = useState("idle"); // idle | building | ready
  const [videoInfo, setVideoInfo] = useState(null);
  const [progress, setProgress] = useState(0);
  const [queueInfo, setQueueInfo] = useState(null); // {position, total} when waiting in line
  const [eta, setEta] = useState(null);
  const [cancelWindow, setCancelWindow] = useState(false);
  const [stalled, setStalled] = useState(false);
  const [showTips, setShowTips] = useState(false);
  const [savedLib, setSavedLib] = useState(false);
  const [showDoneModal, setShowDoneModal] = useState(false);
  const [socialOpen, setSocialOpen] = useState(false);
  const [socialHook, setSocialHook] = useState("");
  const [socialCaption, setSocialCaption] = useState("");
  const [socialDrafting, setSocialDrafting] = useState(false);
  const [socialCreating, setSocialCreating] = useState(false);
  const [renderTimes, setRenderTimes] = useState(() => {
    try { return JSON.parse(localStorage.getItem("sms_render_times") || "[]"); } catch { return []; }
  });
  const lastPRef = useRef({ p: 0, t: 0 });
  const startRef = useRef(0);
  const estRef = useRef(0);
  const recordedCount = (manual?.steps || []).filter((s) => s.user_voice_id).length;
  const pollingRef = useRef(false);
  const autoRetriedRef = useRef(0);
  const [narration, setNarration] = useState(true);
  const [voiceSource, setVoiceSource] = useState("ai"); // ai | mine
  const [showStudio, setShowStudio] = useState(false);
  const [music, setMusic] = useState(false);
  const [liveElapsed, setLiveElapsed] = useState(0);
  const [comfortIdx, setComfortIdx] = useState(0);
  const [customMusic, setCustomMusic] = useState(null); // { name, data } one-time upload
  const [savedTracks, setSavedTracks] = useState([]); // [{id,name}] saved on account
  const [selectedTrackId, setSelectedTrackId] = useState(null); // chosen saved track
  const [saveSong, setSaveSong] = useState(false); // "Save Song?" for a fresh upload
  // STEP NINE audio mix — mirrors the same keys used in the Settings modal (kept in unison)
  const MIX_DEFS = { music_gain: 3, voice_gain: 8, intro_gain: 6, outro_gain: 6 };
  const [mix, setMix] = useState({ ...MIX_DEFS, music_muted: false, voice_muted: false });
  const [mixSaving, setMixSaving] = useState(false);
  useEffect(() => {
    const s = user?.settings || {};
    setMix({
      music_gain: s.music_gain ?? MIX_DEFS.music_gain,
      voice_gain: s.voice_gain ?? MIX_DEFS.voice_gain,
      intro_gain: s.intro_gain ?? MIX_DEFS.intro_gain,
      outro_gain: s.outro_gain ?? MIX_DEFS.outro_gain,
      music_muted: !!s.music_muted,
      voice_muted: !!s.voice_muted,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.settings?.music_gain, user?.settings?.voice_gain, user?.settings?.intro_gain, user?.settings?.outro_gain, user?.settings?.music_muted, user?.settings?.voice_muted]);
  const saveMix = async (patch) => {
    setMix((m) => ({ ...m, ...patch }));
    setMixSaving(true);
    try {
      await api.put("/settings", patch);
      await refresh();
    } catch {
      toast.error("Couldn't save the audio mix — try again");
    } finally {
      setMixSaving(false);
    }
  };
  const bumpGain = (key, delta) => {
    const cur = mix[key] ?? MIX_DEFS[key];
    saveMix({ [key]: Math.max(1, Math.min(10, cur + delta)) });
  };
  const toggleMixMute = (key) => saveMix({ [key]: !mix[key] });
  const [voiceGender, setVoiceGender] = useState("female");
  const [voicePitch, setVoicePitch] = useState("normal"); // normal | higher | kid
  const [previewLoading, setPreviewLoading] = useState(false);
  const previewVoice = async () => {
    if (previewLoading) return;
    try {
      setPreviewLoading(true);
      const selected = elVoices.find((v) => v.voice_id === narrationVoiceId);
      const useVoiceId = !!(selected && !selected.is_cloned);
      const payload = useVoiceId
        ? { voice_id: narrationVoiceId, voice_gender: voiceGender, voice_pitch: voicePitch }
        : { voice_gender: voiceGender, voice_pitch: voicePitch };
      const { data } = await api.post("/tts/preview", payload, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const audio = new Audio(url);
      audio.onended = () => URL.revokeObjectURL(url);
      await audio.play();
    } catch (e) {
      toast.error("Couldn't play a voice sample — please try again.");
    } finally {
      setPreviewLoading(false);
    }
  };
  const [narrationVoiceId, setNarrationVoiceId] = useState("");
  const voiceAudioRef = useRef(null);
  const [playingVoiceId, setPlayingVoiceId] = useState(null);
  const playSample = async (e, v) => {
    e.stopPropagation();
    try {
      if (voiceAudioRef.current) { voiceAudioRef.current.pause(); voiceAudioRef.current = null; }
      let url, revoke = false;
      if (v.preview_url) {
        url = v.preview_url;
      } else {
        const { data } = await api.post("/tts/preview", { voice_id: v.is_cloned ? undefined : v.voice_id, voice_gender: v.gender === "male" ? "male" : "female", voice_pitch: "normal" }, { responseType: "blob" });
        url = URL.createObjectURL(data);
        revoke = true;
      }
      const audio = new Audio(url);
      voiceAudioRef.current = audio;
      setPlayingVoiceId(v.voice_id);
      audio.onended = () => { setPlayingVoiceId(null); if (revoke) URL.revokeObjectURL(url); };
      await audio.play();
    } catch (err) {
      setPlayingVoiceId(null);
      toast.error("Couldn't play a voice sample — please try again.");
    }
  };
  const [elVoices, setElVoices] = useState([]);
  const [showThumb, setShowThumb] = useState(false);
  const [thumbTitle, setThumbTitle] = useState("");
  const [thumbSourceId, setThumbSourceId] = useState(null);
  const [thumbSourceIdx, setThumbSourceIdx] = useState(null);
  const [thumbExpanded, setThumbExpanded] = useState(false);
  const [thumbBusy, setThumbBusy] = useState(false);
  const [thumbFont, setThumbFont] = useState("anton");
  const [thumbColor, setThumbColor] = useState("#ffffff");
  const [customColor, setCustomColor] = useState("#ffcc00");
  const [thumbPlacement, setThumbPlacement] = useState("bottom");
  const [thumbShift, setThumbShift] = useState("");   // "" | up | down | left | right — nudge subject in frame
  const [thumbOutline, setThumbOutline] = useState(false);
  const [thumbOutlineColor, setThumbOutlineColor] = useState("#000000");
  const [thumbOutlineCustom, setThumbOutlineCustom] = useState("#1e3aaa");
  const [thumbSize, setThumbSize] = useState("md");   // sm | md | lg | xl — title font size on the thumbnail
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [overlayTitle, setOverlayTitle] = useState("");
  const overlayTitleInit = useRef(false);
  const thumbInit = useRef(false);
  const thumbTitleInit = useRef(false);
  const musicInit = useRef(false);
  const completionTplTriggered = useRef(false);
  const [candidates, setCandidates] = useState(null); // null = loading; [] or list
  const [openMap, setOpenMap] = useState({});   // collapsible step cards; default open
  const stepOpen = (k) => openMap[k] !== false;
  const toggleStep = (k) => setOpenMap((m) => ({ ...m, [k]: m[k] === false }));
  const STEP_KEYS = ["s5", "s6", "s7", "s8", "s9", "s10", "s11", "s12", "s13", "s14", "s15", "s16"];
  const anyStepOpen = STEP_KEYS.some((k) => openMap[k] !== false);
  const toggleAllSteps = () => {
    const next = {};
    STEP_KEYS.forEach((k) => { next[k] = anyStepOpen ? false : true; });
    setOpenMap(next);
  };
  const StepToggle = ({ k }) => (
    <span className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 text-xs font-extrabold text-white/90" data-testid={`step-toggle-${k}`}>
      {stepOpen(k) ? "Close" : "Open"}
    </span>
  );
  // Two-column title so the em-dash lines up dead-centre on every stripe: left half right-aligned,
  // dash pinned to the middle, right half left-aligned.
  const StepTitle = ({ left, right, testid }) => (
    <span {...(testid ? { "data-testid": testid } : {})} className="flex w-full items-center font-extrabold text-xl tracking-wide text-white">
      <span className="flex-1 text-right pr-2 whitespace-nowrap">{left}</span>
      <span className="shrink-0 px-1">—</span>
      <span className="flex-1 text-left pl-2 pr-10">{right}</span>
    </span>
  );
  const [expertConfirm, setExpertConfirm] = useState(false);
  const [expertBusy, setExpertBusy] = useState(false);
  const buyExpertPack = async () => {
    if (expertBusy) return;
    setExpertBusy(true);
    try {
      const { data } = await api.post(`/manuals/${id}/expert/enable`);
      setManual((m) => (m ? { ...m, expert: data.expert } : m));
      toast.success(manual?.expert?.enabled ? "Added 500 more questions!" : "AI Expert is live on this video! 500 questions ready.");
      setExpertConfirm(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add the Expert — check your balance.");
    } finally {
      setExpertBusy(false);
    }
  };
  const [candErr, setCandErr] = useState(false);
  const [urlColor, setUrlColor] = useState("white");
  const [urlCustomColor, setUrlCustomColor] = useState("#ffd21e");
  const [useIntro, setUseIntro] = useState(true);
  const [thumbPreview, setThumbPreview] = useState(null);
  const [thumbSaved, setThumbSaved] = useState(null);
  const uploadThumbRef = useRef(null);
  const [thumbUploading, setThumbUploading] = useState(false);
  const [musicStart, setMusicStart] = useState(0);
  const [caption, setCaption] = useState(null);      // { caption, hashtags } once generated
  const [captionBusy, setCaptionBusy] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const [pdfLang, setPdfLang] = useState(null);
  const [textOverlay, setTextOverlay] = useState(true);
  const [autoImageMotion, setAutoImageMotion] = useState(false);  // "Add Image Movement" — auto medium glide on split shots; default OFF
  const [orientation, setOrientation] = useState("landscape");  // 16:9 default; 9:16 portrait available via the aspect picker
  const [videoLang, setVideoLang] = useState(() => {
    try { return localStorage.getItem("sms_video_lang") || "en"; } catch { return "en"; }
  });
  const [videoVariants, setVideoVariants] = useState({});
  const [langCopyBusy, setLangCopyBusy] = useState(false);
  const langCopyPreselectRef = useRef(false);
  // A language-copy draft lands here pre-set to its target language.
  useEffect(() => {
    if (manual?.lang_copy_target && !langCopyPreselectRef.current) {
      langCopyPreselectRef.current = true;
      setVideoLang(manual.lang_copy_target);
    }
  }, [manual?.lang_copy_target]);
  const makeLanguageCopy = async (lang) => {
    if (langCopyBusy) return;
    setLangCopyBusy(true);
    try {
      const { data } = await api.post(`/manuals/${id}/language-copy`, { language: lang });
      toast.success(`Created a ${LANGS[lang]?.label || lang.toUpperCase()} copy — render it here 🎬`);
      navigate(`/manual/${data.id}/complete`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create the copy — try again");
    } finally { setLangCopyBusy(false); }
  };
  const [lastOrient, setLastOrient] = useState(null);
  const [heroZoom, setHeroZoom] = useState(false);
  const [previewImg, setPreviewImg] = useState(null); // { id, index } for the step-photo lightbox
  const [reloadingImgs, setReloadingImgs] = useState(false); // "Reload Images" tile busy state
  const [clipEditIdx, setClipEditIdx] = useState(null);
  const [ccEditIdx, setCcEditIdx] = useState(null);
  const [imgBust, setImgBust] = useState(0);                 // cache-buster to force gallery <img> re-requests
  const [fixText, setFixText] = useState(""); // targeted fix instruction typed in the lightbox
  const [fixZoom, setFixZoom] = useState(""); // reframe/zoom choice for the redo
  const [scanResults, setScanResults] = useState({}); // index -> { ok, reason }
  const [scanning, setScanning] = useState(false);
  const [insistFix, setInsistFix] = useState(false); // soft-lock override for a photo that passed QA
  const [heroDl, setHeroDl] = useState(false);
  const [stillsDl, setStillsDl] = useState(false);
  const [pptxBusy, setPptxBusy] = useState(false);
  const [showPptxGuide, setShowPptxGuide] = useState(false);
  const [handoutBusy, setHandoutBusy] = useState(false);
  const [dlAll, setDlAll] = useState(null); // { label, mb, pct, done } while "Download All" runs
  const [assetsDownloaded, setAssetsDownloaded] = useState(() => {
    try { return localStorage.getItem(`sms_assets_dl_${id}`) === "1"; } catch { return false; }
  }); // persists across visits (per manual)
  const [answerKeyBusy, setAnswerKeyBusy] = useState(false);

  const downloadPptx = async () => {
    setPptxBusy(true);
    try {
      const res = await api.get(`/manuals/${id}/pptx`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `stepmystep-${(manual.title || "manual").replace(/[^a-z0-9]+/gi, "-").toLowerCase().slice(0, 50)}.pptx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Keynote downloaded — open it in Keynote, Google Slides or any slideshow app to present or edit.");
    } catch {
      toast.error("Couldn't build the Keynote — please try again.");
    } finally {
      setPptxBusy(false);
    }
  };

  const downloadHandout = async () => {
    setHandoutBusy(true);
    try {
      const res = await api.get(`/manuals/${id}/handout.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `stepmystep-${(manual.title || "manual").replace(/[^a-z0-9]+/gi, "-").toLowerCase().slice(0, 50)}-handout.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Teacher handout downloaded — print it out for the class.");
    } catch {
      toast.error("Couldn't build the handout — please try again.");
    } finally {
      setHandoutBusy(false);
    }
  };

  const downloadAllAssets = async () => {
    if (dlAll && !dlAll.done) return;
    const slug = (manual.title || "stepmystep").replace(/[^a-z0-9]+/gi, "-").toLowerCase().slice(0, 40);
    setDlAll({ label: "Building your kit…", mb: 0, pct: 6, done: false });
    try {
      const res = await api.get(`/community/${id}/kit.zip`, {
        responseType: "blob",
        onDownloadProgress: (e) => {
          setDlAll({ label: "Downloading your kit…", mb: (e.loaded || 0) / 1048576, pct: 40, done: false });
        },
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `stepmystep-${slug}-kit.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 3000);
      setDlAll({ label: "Success! All digital assets downloaded!", mb: (res.data.size || 0) / 1048576, pct: 100, done: true });
      try { localStorage.setItem(`sms_assets_dl_${id}`, "1"); } catch {}
      setAssetsDownloaded(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't build the kit — please try again.");
      setDlAll(null);
      return;
    }
    setTimeout(() => setDlAll(null), 8000);
  };


  const downloadAnswerKey = async () => {
    setAnswerKeyBusy(true);
    try {
      const res = await api.get(`/manuals/${id}/answer-key.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `stepmystep-${(manual.title || "manual").replace(/[^a-z0-9]+/gi, "-").toLowerCase().slice(0, 50)}-answer-key.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Answer key downloaded — the completed teacher copy.");
    } catch {
      toast.error("Couldn't build the answer key — please try again.");
    } finally {
      setAnswerKeyBusy(false);
    }
  };
  const [amazonTag, setAmazonTag] = useState("");
  const [amazonBusy, setAmazonBusy] = useState(false);
  const musicFileRef = useRef(null);
  const previewRef = useRef(null);
  const [previewingMusic, setPreviewingMusic] = useState(false);
  const jingled = useRef(false);
  const [regenSet, setRegenSet] = useState({}); // step index -> true while regenerating
  const [twoPeople, setTwoPeople] = useState(false);
  const [castHint, setCastHint] = useState("");
  const [noPeople, setNoPeople] = useState(false);
  const [twoBusy, setTwoBusy] = useState(false);

  const applyPeople = async () => {
    if (!manual) return;
    setTwoBusy(true);
    try {
      await api.post(`/manuals/${id}/remix-recipe`, {
        recipe_style: manual.recipe_style || (manual.inspired_by ? "inspired" : "traditional"),
        ...(manual.inspired_by ? { inspired_by: manual.inspired_by } : {}),
        two_people: twoPeople && !noPeople,
        ...(twoPeople && !noPeople && castHint.trim() ? { cast_hint: castHint.trim() } : {}),
        no_people: noPeople,
        restyle_images: true,
        language: manual.language || "en",
      });
      for (let i = 0; i < 90; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        const { data } = await api.get(`/manuals/${id}`);
        if (data.remix_status === "ready") {
          setManual(data);
          setTwoPeople(!!data.two_people);
          setCastHint(data.cast_hint || "");
          setNoPeople(!!data.no_people);
          setVideoState("idle");
          toast.success(data.no_people ? "Done — no people, just the subject ✨ Your step photos refresh below." : "Both people are now locked into every photo ✨ Your step photos refresh below.");
          setTwoBusy(false);
          return;
        }
        if (data.remix_status === "error") { toast.error("Couldn't apply that — please try again."); setTwoBusy(false); return; }
      }
      toast.error("Taking longer than usual — refresh in a moment.");
      setTwoBusy(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't apply — please try again.");
      setTwoBusy(false);
    }
  };

  const setStepThumb = (i, imageId) => {
    setManual((m) => {
      if (!m) return m;
      const steps = (m.steps || []).map((s, idx) => (idx === i ? { ...s, thumbnail_id: imageId } : s));
      return { ...m, steps };
    });
  };

  const setStepMotion = async (i, motion) => {
    setManual((m) => (m ? { ...m, steps: (m.steps || []).map((s, idx) => (idx === i ? { ...s, motion } : s)) } : m));
    try {
      await api.post(`/manuals/${id}/steps/${i}/motion`, { motion });
    } catch {
      toast.error("Couldn't save that movement — please try again.");
    }
  };

  const loadSavedTracks = useCallback(async () => {
    try { const { data } = await api.get("/me/music"); setSavedTracks(data.tracks || []); } catch { /* noop */ }
  }, []);
  useEffect(() => { loadSavedTracks(); }, [loadSavedTracks]);

  const deleteSavedTrack = async (trackId) => {
    try {
      await api.delete(`/me/music/${trackId}`);
      setSavedTracks((t) => t.filter((x) => x.id !== trackId));
      if (selectedTrackId === trackId) setSelectedTrackId(null);
      toast.success("Song removed");
    } catch { toast.error("Couldn't remove that song"); }
  };

  const downloadPdfLang = async (code) => {
    if (pdfLang) return;
    setPdfLang(code);
    try {
      if (SERVER_PDF_LANGS.has(code)) await saveServerPdf(manual, code);
      else await savePdf(manual, code);
    } finally {
      setPdfLang(null);
    }
  };

  const deleteRenderTime = (idx) => {
    setRenderTimes((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      try { localStorage.setItem("sms_render_times", JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  const scanPhotos = async () => {
    if (scanning) return;
    setScanning(true);
    try {
      const { data } = await api.post(`/manuals/${id}/steps/scan`);
      const map = {};
      (data.results || []).forEach((r) => { map[r.index] = { ok: r.ok, reason: r.reason }; });
      setScanResults(map);
      const issues = (data.results || []).filter((r) => !r.ok).length;
      toast.success(issues ? `Found ${issues} photo${issues > 1 ? "s" : ""} that may need a fix` : "All photos look good! ✓");
    } catch {
      toast.error("Couldn't scan right now — try again in a moment");
    } finally {
      setScanning(false);
    }
  };

  // One-tap true image reload — re-fetch the manual, kick generation for any still-missing step
  // photos, and cache-bust every gallery <img> so a photo that failed to load will re-request.
  const reloadImages = async () => {
    if (reloadingImgs) return;
    setReloadingImgs(true);
    try {
      const { data } = await api.get(`/manuals/${id}`);
      setManual(data);
      const missing = (data.steps || []).map((s, i) => (s.thumbnail_id ? null : i)).filter((i) => i !== null);
      if (missing.length) {
        await Promise.all(missing.map((i) => api.post(`/manuals/${id}/steps/${i}/image`).catch(() => {})));
        try { const { data: fresh } = await api.get(`/manuals/${id}`); setManual(fresh); } catch { /* keep current */ }
      }
      setImgBust(Date.now());
      toast.success(missing.length ? "Fetched the missing photos ✨" : "Images reloaded ✨");
    } catch {
      toast.error("Couldn't reload images — try again in a moment.");
    } finally {
      setReloadingImgs(false);
    }
  };


  const regenerateStep = async (i, instruction, zoom) => {
    if (regenSet[i]) return;
    setRegenSet((r) => ({ ...r, [i]: true }));
    try {
      const { data } = await api.post(`/manuals/${id}/steps/${i}/regenerate-image`, { instruction: instruction || null, zoom: zoom || null });
      if (data.image_id) {
        setManual((m) => {
          if (!m) return m;
          const steps = (m.steps || []).map((s, idx) => (idx === i ? {
            ...s,
            thumbnail_id: data.image_id,
            redone: true,
            redo_count: data.redo_count ?? ((s.redo_count || 0) + 1),
            original_thumbnail_id: data.original_thumbnail_id,
            redo_thumbnail_id: data.redo_thumbnail_id,
          } : s));
          return { ...m, steps, redos_used: (m.redos_used || 0) + 1 };
        });
        if (data.charged_cents) toast(`Extra redo — $${(data.charged_cents / 100).toFixed(2)} deducted`);
        setScanResults((sr) => { const n = { ...sr }; delete n[i]; return n; });
        setInsistFix(false);
        setPreviewImg((p) => (p && p.index === i ? { ...p, id: data.image_id } : p));
        toast.success(`Step ${i + 1} photo updated — tap Original / New to pick your favourite`);
        return data.image_id;
      }
      toast.error("Couldn't regenerate that photo — try again");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't regenerate — try again in a moment");
    } finally {
      setRegenSet((r) => { const n = { ...r }; delete n[i]; return n; });
    }
  };

  const toggleStepImage = async (i, use) => {
    try {
      const { data } = await api.post(`/manuals/${id}/steps/${i}/image/toggle`, { use });
      setManual((m) => {
        if (!m) return m;
        const steps = (m.steps || []).map((s, idx) => (idx === i ? { ...s, thumbnail_id: data.image_id } : s));
        return { ...m, steps };
      });
      setPreviewImg((p) => (p && p.index === i ? { ...p, id: data.image_id } : p));
    } catch {
      toast.error("Couldn't switch versions — try again");
    }
  };

  const flipPhoto = async (i) => {
    setRegenSet((r) => ({ ...r, [i]: true }));
    try {
      const { data } = await api.post(`/manuals/${id}/steps/${i}/flip-photo`);
      setManual((m) => {
        if (!m) return m;
        const steps = (m.steps || []).map((s, idx) => (idx === i ? { ...s, thumbnail_id: data.image_id, original_thumbnail_id: data.original_thumbnail_id } : s));
        return { ...m, steps };
      });
      setPreviewImg((p) => (p && p.index === i ? { ...p, id: data.image_id } : p));
      toast.success("Photo flipped — no redo used");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't flip that photo — try again");
    } finally {
      setRegenSet((r) => { const n = { ...r }; delete n[i]; return n; });
    }
  };

  const reusePhoto = async (i, sourceIndex) => {
    setRegenSet((r) => ({ ...r, [i]: true }));
    try {
      const { data } = await api.post(`/manuals/${id}/steps/${i}/use-photo`, { source_index: sourceIndex });
      setManual((m) => {
        if (!m) return m;
        const steps = (m.steps || []).map((s, idx) => (idx === i ? { ...s, thumbnail_id: data.image_id, original_thumbnail_id: data.original_thumbnail_id } : s));
        return { ...m, steps };
      });
      setPreviewImg((p) => (p && p.index === i ? { ...p, id: data.image_id } : p));
      toast.success(`Now using Step ${sourceIndex + 1}'s photo — no redo used`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't reuse that photo — try again");
    } finally {
      setRegenSet((r) => { const n = { ...r }; delete n[i]; return n; });
    }
  };

  // Replace a step's photo with the creator's OWN uploaded image (free, no redo). Downscaled
  // client-side so the upload stays small, then stored server-side and shown in the gallery.
  const uploadStepPhoto = async (i, file) => {
    if (!file || regenSet[i]) return;
    if (!/^image\//.test(file.type)) { toast.error("Please choose an image file"); return; }
    if (file.size > 25 * 1024 * 1024) { toast.error("That image is too large — please pick one under 25MB"); return; }
    setRegenSet((r) => ({ ...r, [i]: true }));
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = () => {
          try {
            const max = 1600;
            let { width, height } = img;
            if (width > max || height > max) {
              const scale = Math.min(max / width, max / height);
              width = Math.round(width * scale);
              height = Math.round(height * scale);
            }
            const canvas = document.createElement("canvas");
            canvas.width = width; canvas.height = height;
            canvas.getContext("2d").drawImage(img, 0, 0, width, height);
            URL.revokeObjectURL(url);
            resolve(canvas.toDataURL("image/jpeg", 0.9));
          } catch (err) { reject(err); }
        };
        img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("bad image")); };
        img.src = url;
      });
      const { data } = await api.post(`/manuals/${id}/steps/${i}/upload-photo`, { data_url: dataUrl });
      if (data.image_id) {
        setManual((m) => {
          if (!m) return m;
          const steps = (m.steps || []).map((s, idx) => (idx === i ? { ...s, thumbnail_id: data.image_id, original_thumbnail_id: data.original_thumbnail_id, uploaded_photo: true } : s));
          return { ...m, steps };
        });
        setScanResults((sr) => { const n = { ...sr }; delete n[i]; return n; });
        setPreviewImg((p) => (p && p.index === i ? { ...p, id: data.image_id } : p));
        toast.success(`Your photo is in — Step ${i + 1} updated`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't upload that photo — try again");
    } finally {
      setRegenSet((r) => { const n = { ...r }; delete n[i]; return n; });
    }
  };

  const pollVideoStatus = useCallback(() => {
    if (pollingRef.current) return;
    pollingRef.current = true;
    let misses = 0;
    const tick = async () => {
      if (!pollingRef.current) return;
      try {
        const { data } = await api.get(`/manuals/${id}/video/status`);
        misses = 0;
        if (data.status === "queued") {
          setQueueInfo({ position: data.queue_position, total: data.queue_total });
          if (pollingRef.current) setTimeout(tick, 2000);
          return;
        }
        setQueueInfo(null);
        const p = data.progress ?? 0;
        setProgress(p);
        if (p > lastPRef.current.p) { lastPRef.current = { p, t: Date.now() }; setStalled(false); }
        if (p >= 12 && p < 100 && startRef.current) {
          const elapsed = (Date.now() - startRef.current) / 1000;
          const proj = Math.round((elapsed * (100 - p)) / p);
          const capped = Math.min(estRef.current || 180, Math.max(2, proj));
          // Only ever LOWER the estimate — never let the countdown climb back up.
          setEta((prev) => (prev == null ? capped : Math.min(prev, capped)));
        }
        if (data.status === "ready") {
          pollingRef.current = false;
          setProgress(100);
          setEta(null);
          if (startRef.current) {
            const secs = Math.round((Date.now() - startRef.current) / 1000);
            if (secs >= 2 && secs < 3600) {
              setRenderTimes((prev) => {
                const entry = { t: secs, steps: (data.steps || manual?.steps?.length || null) };
                const next = [entry, ...prev].slice(0, 7);
                try { localStorage.setItem("sms_render_times", JSON.stringify(next)); } catch { /* ignore */ }
                return next;
              });
            }
          }
          setVideoInfo({ steps: data.steps, duration: data.duration, narration: data.narration, music: data.music });
          if (data.variants) setVideoVariants(data.variants);
          setLastOrient(data.orientation || null);
          setVideoState("ready");
          // Done means done — the finished video is already persisted server-side, so auto-file it in
          // the user's archive and celebrate. No extra button tap required; a completed render is safe.
          setSavedLib(true);
          setShowDoneModal(true);
          playBell();
          toast.success("Success! Your video is now in your Video Archive 🎉", { duration: 6000 });
          await refresh();
          return;
        }
        if (data.status === "error") {
          pollingRef.current = false;
          setEta(null);
          const msg = data.error || "";
          // A too-long render is auto-stopped server-side with no charge. Restart it once
          // automatically so slower connections/hardware still get their video.
          if (/too long|took too long/i.test(msg) && autoRetriedRef.current < 2) {
            autoRetriedRef.current += 1;
            toast("Rendering took a while — restarting automatically, no charge 🔄", { icon: "🔄" });
            retryVideo();
            return;
          }
          toast.error(msg || "Couldn't compile the video");
          setVideoState("idle");
          return;
        }
      } catch {
        misses += 1;
        if (misses >= 3) setStalled(true);  // surface Retry on flaky wifi
        if (misses > 10) { pollingRef.current = false; setVideoState("idle"); toast.error("Lost connection while building. Please try again."); return; }
      }
      if (pollingRef.current) setTimeout(tick, 1500);
    };
    tick();
  }, [id, refresh]);

  useEffect(() => {
    if (videoState !== "building") { setStalled(false); setLiveElapsed(0); setComfortIdx(0); return; }
    const iv = setInterval(() => setEta((e) => (e == null ? e : Math.max(1, e - 1))), 1000);
    // Live stopwatch that counts up during the render so the user can follow along.
    const liveIv = setInterval(() => {
      if (startRef.current) setLiveElapsed((Date.now() - startRef.current) / 1000);
    }, 250);
    // Rotate the words of comfort every ~4.5s.
    const comfortIv = setInterval(() => setComfortIdx((i) => i + 1), 4500);
    // If progress hasn't advanced in ~22s (and we're not at the finish line), offer a Retry.
    const stallIv = setInterval(() => {
      if (progress < 94 && Date.now() - lastPRef.current.t > 22000) setStalled(true);
    }, 3000);
    return () => { clearInterval(iv); clearInterval(liveIv); clearInterval(comfortIv); clearInterval(stallIv); };
  }, [videoState, progress]);

  useEffect(() => {
    api.get("/config").then(({ data }) => setAmazonTag((prev) => prev || (data.amazon_affiliate_tag || ""))).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/voices").then(({ data }) => setElVoices(data.voices || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!narrationVoiceId && elVoices.length) {
      const firstCurated = elVoices.find((v) => !v.is_cloned) || elVoices[0];
      if (firstCurated) setNarrationVoiceId(firstCurated.voice_id);
    }
  }, [elVoices, narrationVoiceId]);

  useEffect(() => {
    api.get(`/manuals/${id}`).then(({ data }) => {
      setManual(data);
      if (!overlayTitleInit.current) {
        setOverlayTitle(data.video_overlay_title || data.title || "");
        overlayTitleInit.current = true;
      }
      // Reuse the thumbnail from a previous version: restore the saved composed cover so a re-render
      // automatically keeps the same intro title card (no need to rebuild it in the composer).
      if (!thumbInit.current && data.custom_cover_id) {
        setThumbSaved(data.custom_cover_id);
        thumbInit.current = true;
      }
      // Pre-fill the thumbnail title from the saved cover so re-entering feels "already done".
      if (!thumbTitleInit.current && data.custom_cover_title) {
        setThumbTitle(data.custom_cover_title);
        thumbTitleInit.current = true;
      }
      // Restore the music choice from the last render: flip Music ON and re-select the saved song
      // so it doesn't look like nothing was chosen when the video already has a song embedded.
      if (!musicInit.current && data.video_music) {
        setMusic(true);
        if (data.video_music_track_id) setSelectedTrackId(data.video_music_track_id);
        musicInit.current = true;
      }
      setTwoPeople(!!data.two_people);
      setCastHint(data.cast_hint || "");
      setNoPeople(!!data.no_people);
      setAmazonTag(data.amazon_affiliate_tag || "");
      if (data.narration_voice_id) setNarrationVoiceId(data.narration_voice_id);
      if (data.video_variants) {
        setVideoVariants(Object.fromEntries(Object.entries(data.video_variants).map(([k, v]) => [k, { ready: true, duration: v.duration, steps: v.steps }])));
      }
      if (data.video_status === "rendering") {
        const p = data.video_progress || 0;
        setProgress(p);
        const n = (data.steps || []).length || 8;
        estRef.current = Math.round(20 + n * 4);
        startRef.current = Date.now() - (p / 100) * (estRef.current * 1000);
        setEta(Math.max(2, Math.round(estRef.current * (1 - p / 100))));
        setVideoState("building");
        pollVideoStatus();
      }
      if (!jingled.current) {
        jingled.current = true;
        playDoneJingle();
      }
      // One-time: if templating is on but the big finish shot wasn't made from the hero yet, regenerate
      // it so the all-done cover matches the same image group (hero + steps).
      if (data.use_hero_template && !data.completion_template_done && !completionTplTriggered.current) {
        completionTplTriggered.current = true;
        api.post(`/manuals/${id}/complete`).then(({ data: c }) => {
          if (c?.completion_image_id) {
            setManual((prev) => (prev ? { ...prev, completion_image_id: c.completion_image_id, completion_template_done: true } : prev));
          }
        }).catch(() => {});
      }
    }).catch(() => navigate("/"));
    return () => { pollingRef.current = false; };
  }, [id, navigate, pollVideoStatus]);

  // STEP ELEVEN — AI-suggested Amazon product candidates (text only, curated by the creator).
  useEffect(() => {
    setCandidates(null); setCandErr(false);
    api.get(`/manuals/${id}/product-candidates`)
      .then(({ data }) => setCandidates(data.candidates || []))
      .catch(() => { setCandidates([]); setCandErr(true); });
  }, [id]);

  const toggleCandidate = async (cid, next) => {
    setCandidates((cs) => cs.map((c) => (c.id === cid ? { ...c, approved: next } : c)));
    try {
      await api.post(`/manuals/${id}/product-candidates/${cid}`, { approved: next });
    } catch {
      setCandidates((cs) => cs.map((c) => (c.id === cid ? { ...c, approved: !next } : c)));
      toast.error("Couldn't update — try again.");
    }
  };

  const setAllCandidates = async (next) => {
    const targets = candidates.filter((c) => !!c.approved !== next).map((c) => c.id);
    if (!targets.length) return;
    setCandidates((cs) => cs.map((c) => ({ ...c, approved: next })));
    try {
      await Promise.all(targets.map((cid) => api.post(`/manuals/${id}/product-candidates/${cid}`, { approved: next })));
    } catch {
      try { const { data } = await api.get(`/manuals/${id}/product-candidates`); setCandidates(data.candidates || []); } catch { /* keep optimistic */ }
      toast.error("Couldn't update all — try again.");
    }
  };

  const regenerateCandidates = async () => {
    setCandidates(null);
    try {
      const { data } = await api.get(`/manuals/${id}/product-candidates?regenerate=true`);
      setCandidates(data.candidates || []);
    } catch {
      setCandidates([]); toast.error("Couldn't refresh — try again.");
    }
  };


  // Gentle guard against an accidental tab close / trackpad back-swipe while a render is still
  // building. The render continues server-side and auto-lands in My Videos regardless — this is
  // just a nudge so a stray swipe doesn't feel like lost work.
  useEffect(() => {
    if (videoState !== "building") return;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [videoState]);

  // Lazy-load any missing step photos so the "Preview Before Publish" gallery is complete.
  // These are the same images the video render needs anyway, so there's no NET extra cost.
  const previewLoadedRef = useRef(null);
  useEffect(() => {
    if (!manual?.id || previewLoadedRef.current === manual.id) return;
    const steps = manual.steps || [];
    if (!steps.length) return;
    if (manual.moviestar && !manual.moviestar_step_photos) return; // no step photos for this manual
    previewLoadedRef.current = manual.id;
    let cancelled = false;
    (async () => {
      // Pre-generate missing photos with bounded parallelism (backend caps real concurrency),
      // so the "heavy" image work is done before Create Video — making the render feel instant.
      const missing = steps.map((s, i) => (s.thumbnail_id ? null : i)).filter((i) => i !== null);
      const POOL = 8;
      let cursor = 0;
      const worker = async () => {
        while (!cancelled && cursor < missing.length) {
          const i = missing[cursor++];
          try {
            const { data } = await api.post(`/manuals/${id}/steps/${i}/image`);
            if (!cancelled && data.image_id) setStepThumb(i, data.image_id);
          } catch { /* skip on failure */ }
        }
      };
      await Promise.all(Array.from({ length: Math.min(POOL, missing.length) }, worker));
    })();
    return () => { cancelled = true; };
  }, [manual?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!manual) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 size={28} className="spin text-[#5d8a52]" /></div>;
  }

  const share = () => shareManual(manual);

  const downloadAmazonLinks = async () => {
    if (amazonBusy) return;
    setAmazonBusy(true);
    toast.loading("Building your Amazon link sheet…", { id: "amzn" });
    try {
      const res = await api.get(`/manuals/${id}/amazon-links.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const slug = (manual?.title || "manual").replace(/[^a-z0-9]+/gi, "-").toLowerCase().slice(0, 50) || "manual";
      a.download = `stepmystep-${slug}-amazon-links.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Amazon link sheet saved", { id: "amzn" });
    } catch (e) {
      toast.error(e?.response?.status === 404 ? "No Amazon links for this video yet." : "Couldn't build the Amazon link sheet", { id: "amzn" });
    } finally {
      setAmazonBusy(false);
    }
  };

  const createVideo = async (orient = orientation) => {
    if (music && !selectedTrackId && !customMusic) {
      toast.error("Choose a saved song or upload an MP3 — or turn Music off.");
      return;
    }
    if (orient !== orientation) setOrientation(orient);
    setProgress(0);
    setEta(null);
    setStalled(false);
    lastPRef.current = { p: 0, t: Date.now() };
    startRef.current = Date.now();
    const n = (manual?.steps || []).length || 8;
    estRef.current = Math.round(narration ? 25 + n * 4 : 15 + n * 2);
    setEta(estRef.current);
    setVideoState("building");
    setCancelWindow(true);
    setTimeout(() => setCancelWindow(false), 20000);
    try {
      const payload = { narration, music, text_overlay: textOverlay, orientation: orient, language: videoLang };
      // "Add Image Movement" (auto glide) disabled — it pushed renders past 8 min. DIY/Social per-step motion unaffected.
      payload.auto_image_motion = false;
      // Punch-in cut-ins are now baked in automatically (landscape, odd-numbered steps) — no creator config.
      payload.overlay_title = overlayTitle.trim();
      const introThumb = thumbSaved;
      payload.intro_card = !!(useIntro && introThumb);
      if (payload.intro_card) payload.intro_image_id = introThumb;
      if (narration) {
        payload.voice_source = voiceSource;
        if (voiceSource === "ai") {
          payload.voice_pitch = voicePitch;
          const sel = elVoices.find((v) => v.voice_id === narrationVoiceId);
          if (elVoices.length > 0 && narrationVoiceId) {
            payload.narration_voice_id = narrationVoiceId;
            payload.voice_gender = sel?.gender === "male" ? "male" : "female";
          } else {
            payload.voice_gender = voiceGender;
          }
        }
      }
      if (music) {
        payload.music_source = "yours";
        payload.music_start = Math.max(0, Math.round(musicStart));
        if (selectedTrackId) {
          payload.music_track_id = selectedTrackId;
        } else if (customMusic) {
          payload.custom_music = customMusic.data;
          if (saveSong && savedTracks.length < 5) {
            try {
              const { data } = await api.post("/me/music", { name: customMusic.name, data: customMusic.data });
              setSavedTracks((t) => [...t, { id: data.id, name: data.name }]);
              payload.music_track_id = data.id;   // remember this song so re-opening the editor pre-selects it
              setSelectedTrackId(data.id);
              setSaveSong(false);
            } catch { /* keep rendering even if save fails */ }
          }
        }
      }
      await api.post(`/manuals/${id}/video`, payload);
      pollVideoStatus();
    } catch (e) {
      if (e?.response?.status === 402) {
        toast.error(e.response.data.detail);
        navigate("/pricing");
      } else {
        toast.error(e?.response?.data?.detail || "Couldn't start the video");
      }
      setVideoState("idle");
    }
  };

  const cancelVideo = async () => {
    pollingRef.current = false;
    setVideoState("idle");
    setCancelWindow(false);
    try {
      await api.post(`/manuals/${id}/video/cancel`);
      toast.success("Render cancelled — you weren't charged.");
    } catch {
      toast.message("Render stopped.");
    }
  };

  const retryVideo = async () => {
    pollingRef.current = false;
    setStalled(false);
    // Reset the on-screen stopwatch to 0:00 for the fresh attempt.
    startRef.current = Date.now();
    setLiveElapsed(0);
    setProgress(0);
    setEta(null);
    try { await api.post(`/manuals/${id}/video/cancel`); } catch { /* clear the stuck job */ }
    createVideo();  // fresh attempt — credits are only charged on a successful render
  };

  const pickMusicFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const okType = /audio\/(mpeg|mp3)/.test(file.type) || /\.mp3$/i.test(file.name);
    if (!okType) { toast.error("Please upload an MP3 file"); return; }
    if (file.size > 12 * 1024 * 1024) { toast.error("File too large — keep it under ~1 minute"); return; }
    const reader = new FileReader();
    reader.onload = () => {
      setCustomMusic({ name: file.name, data: String(reader.result) });
      setSelectedTrackId(null);
      if (videoState === "ready") setVideoState("idle");
    };
    reader.onerror = () => toast.error("Couldn't read that file");
    reader.readAsDataURL(file);
  };

  const previewCustomMusic = () => {
    if (!customMusic) return;
    try {
      if (previewRef.current) { previewRef.current.pause(); previewRef.current = null; setPreviewingMusic(false); return; }
      const a = new Audio(customMusic.data);
      previewRef.current = a;
      setPreviewingMusic(true);
      const stop = () => { try { a.pause(); } catch { /* noop */ } previewRef.current = null; setPreviewingMusic(false); };
      a.play().then(() => setTimeout(stop, 5000)).catch(stop);
      a.onended = stop;
    } catch {
      setPreviewingMusic(false);
    }
  };

  const toggleOpt = (which) => {
    if (which === "narration") setNarration((v) => !v);
    else if (which === "music") setMusic((v) => !v);
    else if (which === "text") setTextOverlay((v) => !v);
    if (videoState === "ready") setVideoState("idle"); // options changed — allow a rebuild
  };

  const downloadHero = async () => {
    const heroId = manual?.completion_image_id || manual?.hero_image_id;
    if (!heroId) return;
    setHeroDl(true);
    try {
      const fname = `stepmystep-${(manual.title || "vision").replace(/[^a-z0-9]+/gi, "-").toLowerCase().slice(0, 50)}.jpg`;
      // Navigate straight to the forced-download endpoint. Safari/iOS ignores the anchor
      // `download` attribute on blob URLs (opens a share sheet); Content-Disposition saves directly.
      const a = document.createElement("a");
      a.href = `${imageUrl(heroId)}?dl=1&name=${encodeURIComponent(fname)}`;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      toast.error("Couldn't download the image");
    } finally {
      setHeroDl(false);
    }
  };

  const downloadStills = async () => {
    setStillsDl(true);
    try {
      const res = await api.get(`/manuals/${id}/stills.zip`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `stepmystep-${(manual.title || "manual").replace(/[^a-z0-9]+/gi, "-").toLowerCase().slice(0, 50)}-photos.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Couldn't download your photos");
    } finally {
      setStillsDl(false);
    }
  };

  const downloadVideo = async (key) => {
    try {
      const { lang, orient } = parseVariantKey(key);
      const q = key.includes(":") ? `?variant=${encodeURIComponent(key)}` : `?orientation=${key}`;
      const res = await api.get(`/manuals/${id}/video/download${q}`, { responseType: "blob" });
      const ratio = orient === "landscape" ? "-16x9" : orient === "portrait" ? "-9x16" : "";
      const langsfx = lang && lang !== "en" ? `-${lang}` : "";
      const filename = `stepmystep-${(manual.title || "manual").replace(/[^a-z0-9]+/gi, "-").toLowerCase().slice(0, 50)}${langsfx}${ratio}.mp4`;
      await saveOrShareVideo(res.data, filename, manual.title || "Vittyyo video");
    } catch {
      toast.error("Couldn't download the video");
    }
  };

  const copyPageLink = async () => {
    const url = `${window.location.origin}/manual/${id}/complete`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied — paste it anywhere to jump straight back to this video");
    } catch {
      const ta = document.createElement("textarea");
      ta.value = url; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.focus(); ta.select();
      try { document.execCommand("copy"); toast.success("Link copied — paste it to return to this video"); }
      catch { toast.error("Couldn't copy the link"); }
      ta.remove();
    }
  };

  const composeThumbnail = async () => {
    if (!thumbSourceId) { toast.error("Pick one of your photos first"); return; }
    if (!thumbTitle.trim()) { toast.error("Type your title first"); return; }
    setThumbBusy(true);
    try {
      const { data } = await api.post("/thumbnails/compose", {
        source_image_id: thumbSourceId,
        manual_id: id,
        title: thumbTitle.trim(),
        title_font: thumbFont,
        title_color: thumbColor === "custom" ? customColor : thumbColor,
        title_placement: thumbPlacement,
        img_shift: thumbShift,
        title_outline: thumbOutline,
        title_outline_color: thumbOutlineColor === "custom" ? thumbOutlineCustom : thumbOutlineColor,
        title_size: thumbSize,
        website_url: websiteUrl.trim(),
        url_color: urlColor === "custom" ? urlCustomColor : urlColor,
      });
      if (!data.image_id) { toast.error("Couldn't build the thumbnail — try again"); return; }
      setThumbPreview(data.image_id);
      // Auto-lock the preview in as the cover so there's no missable extra step — every fresh
      // preview immediately becomes the video's dashboard thumbnail.
      try {
        await api.post(`/manuals/${id}/set-thumbnail`, { image_id: data.image_id, website_url: websiteUrl.trim(), title: thumbTitle.trim() });
        setThumbSaved(data.image_id);
        setManual((m) => (m ? { ...m, hero_image_id: data.image_id, completion_image_id: data.image_id, custom_cover_id: data.image_id } : m));
        toast.success("Thumbnail locked in ✓ — it's now your video's cover.");
      } catch {
        setThumbSaved(null);
        toast.error("Preview ready, but couldn't lock it in — tap “Use Thumbnail”.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't build the thumbnail. Try again in a moment.");
    } finally {
      setThumbBusy(false);
    }
  };

  const uploadThumbFile = async (file) => {
    if (!file) return;
    try {
      const dataUrl = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
      setCropSrc(dataUrl);
    } catch { toast.error("Couldn't read that image"); }
  };
  const applyCroppedThumb = async (cropped) => {
    setCropSrc(null);
    setThumbUploading(true);
    try {
      await api.post(`/manuals/${id}/edit`, { thumbnail: cropped });
      const { data: m2 } = await api.get(`/manuals/${id}`);
      setManual(m2);
      if (m2?.hero_image_id) setThumbSaved(m2.hero_image_id);
      toast.success("New thumbnail set ✓ — it's now your video's cover.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save that image — try another.");
    } finally { setThumbUploading(false); }
  };

  const saveThumbnail = async () => {
    if (!thumbPreview) return;
    setThumbBusy(true);
    try {
      await api.post(`/manuals/${id}/set-thumbnail`, { image_id: thumbPreview, website_url: websiteUrl.trim() });
      setThumbSaved(thumbPreview);
      setManual((m) => (m ? { ...m, hero_image_id: thumbPreview, completion_image_id: thumbPreview } : m));
      toast.success("Thumbnail locked in ✓ — it's now your video's cover.");
    } catch {
      toast.error("Couldn't set that thumbnail — try again.");
    } finally {
      setThumbBusy(false);
    }
  };

  const genCaption = async () => {
    setCaptionBusy(true);
    try {
      const { data } = await api.post(`/manuals/${id}/caption`);
      setCaption(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't write a caption — try again.");
    } finally {
      setCaptionBusy(false);
    }
  };

  const copyCaption = async () => {
    if (!caption) return;
    const text = `${caption.caption}`.trim();
    try { await navigator.clipboard.writeText(text); toast.success("Caption copied ✓"); }
    catch { toast.error("Couldn't copy — long-press to select instead."); }
  };

  const downloadThumbnail = async (imageId) => {
    try {
      const fname = `stepmystep-thumbnail-${(manual.title || "video").replace(/[^a-z0-9]+/gi, "-").toLowerCase().slice(0, 40)}.jpg`;
      const a = document.createElement("a");
      a.href = `${imageUrl(imageId)}?dl=1&name=${encodeURIComponent(fname)}`;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      toast.error("Couldn't download that thumbnail");
    }
  };


  return (
    <div className="sms-cmpl-dark max-w-3xl mx-auto px-4 pt-6 pb-16">
      <div className="flex justify-end mb-2"><SpendTicker manualId={id} /></div>

      {showStudio && (
        <VoiceStudioModal
          manual={manual}
          showLabels={user?.settings?.studio_labels !== false}
          onClose={() => setShowStudio(false)}
          onSaved={() => api.get(`/manuals/${id}`).then(({ data }) => setManual(data)).catch(() => {})}
        />
      )}
      {showTips && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={() => setShowTips(false)} data-testid="best-results-modal">
          <div className="sms-tweed bg-[#171622] rounded-3xl w-full max-w-sm shadow-2xl overflow-hidden border border-[#e7defb]"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3.5 bg-[#171426] text-white">
              <div className="flex items-center gap-2 font-extrabold"><Info size={18} className="text-[#a9c39c]" /> For best results</div>
              <button data-testid="best-results-close" onClick={() => setShowTips(false)} className="p-1 rounded-full hover:bg-white/10"><X size={20} /></button>
            </div>
            <div className="px-5 py-5">
              <p className="text-sm text-[#a8a4ba] mb-3">A video takes about a minute to render. To keep it smooth:</p>
              <ul className="space-y-3">
                {[
                  [Wifi, "Use a strong Wi-Fi connection", "Rendering sends and receives a lot — a solid signal avoids hiccups."],
                  [Smartphone, "Set your phone down and let it work", "Leave this screen open. It keeps going even if you switch tabs."],
                  [Square, "Close other heavy apps", "Free up memory so your phone can focus on your video."],
                  [BatteryCharging, "Low battery? Plug in", "Some phones throttle performance in low-power mode."],
                ].map(([Ico, title, desc], i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#f3edff] text-[#4f7746] flex items-center justify-center mt-0.5"><Ico size={16} /></span>
                    <div>
                      <div className="font-bold text-sm text-[#2c2841]">{title}</div>
                      <div className="text-xs text-[#9a96ac] leading-snug">{desc}</div>
                    </div>
                  </li>
                ))}
              </ul>
              <p className="text-center text-[11px] text-[#b3aec6] mt-4">If it ever stalls, just tap <b>Retry</b> — you're never charged for a failed render.</p>
              <button data-testid="best-results-got-it" onClick={() => setShowTips(false)}
                className="mt-4 w-full sms-solid-btn text-white font-bold rounded-xl py-2.5">Got it</button>
            </div>
          </div>
        </div>
      )}
      {expertConfirm && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => !expertBusy && setExpertConfirm(false)} data-testid="expert-confirm-modal">
          <div className="sms-tweed bg-[#171622] rounded-3xl w-full max-w-md shadow-2xl overflow-hidden border-2 border-[#1e3aaa]"
            onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 text-white flex items-center gap-2.5" style={{ background: "#1e3aaa" }}>
              <MessageCircle size={22} />
              <h3 className="text-lg font-extrabold leading-tight">
                {manual.expert?.enabled ? "Add more Expert questions?" : "Add an AI Expert to this video?"}
              </h3>
            </div>
            <div className="px-6 py-5">
              <p className="text-sm text-[#c9d2f0] leading-relaxed">
                {manual.expert?.enabled ? (
                  <>This Expert has used <b className="text-white">{manual.expert.used}/{manual.expert.quota}</b> questions. Add <b className="text-white">500 more</b> for <b className="text-white">$1.99</b>, deducted from your balance now.</>
                ) : (
                  <>Viewers get a friendly, on-topic AI chatbot right on this video — auto-tuned to your topic and locked on-subject. You get <b className="text-white">500 questions for $1.99</b>, deducted from your balance now.</>
                )}
              </p>
              <div className="mt-5 flex gap-2">
                <button data-testid="expert-modal-cancel" onClick={() => setExpertConfirm(false)} disabled={expertBusy}
                  className="flex-1 text-sm font-extrabold text-[#c9d2f0] bg-[#0b0b10] border-2 border-[#3a3a4a] rounded-full py-2.5 hover:bg-black transition-colors disabled:opacity-60">
                  Not now
                </button>
                <button data-testid="expert-modal-confirm" onClick={buyExpertPack} disabled={expertBusy}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 text-sm font-extrabold text-white bg-[#1e3aaa] border-2 border-[#16308c] rounded-full py-2.5 hover:brightness-110 transition-all disabled:opacity-60">
                  {expertBusy ? <Loader2 size={15} className="spin" /> : <Plus size={15} />}
                  {manual.expert?.enabled ? "Add 500 — $1.99" : "Enable — $1.99"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      {showPptxGuide && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto"
          onClick={() => setShowPptxGuide(false)} data-testid="pptx-guide-modal">
          <div className="sms-tweed bg-[#171622] rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden border border-[#e7defb] my-auto"
            onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="relative px-6 py-5 text-white" style={{ background: "linear-gradient(135deg,#9a3412,#c2410c)" }}>
              <button data-testid="pptx-guide-close" onClick={() => setShowPptxGuide(false)}
                className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-white/15 transition-colors"><X size={20} /></button>
              <div className="flex items-center gap-2.5">
                <span className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center shrink-0"><MonitorPlay size={22} /></span>
                <div>
                  <h3 className="text-lg font-extrabold leading-tight">Your Editable Presentation</h3>
                  <p className="text-[13px] font-semibold text-white/85 leading-tight">Works with Keynote &amp; Google Slides</p>
                </div>
              </div>
            </div>
            {/* Body */}
            <div className="px-6 py-5">
              <p className="text-sm text-[#4a475c] mb-4 leading-relaxed">
                Your slides download as a Keynote-ready <b>(.pptx)</b> file. For a flawless open on <b>any</b> device — Mac or PC — run it through Google Slides once. It takes about a minute:
              </p>
              <ol className="space-y-3">
                {[
                  [Download, "Download the presentation", "Tap the button below to save the .pptx file to your device.", "#c2410c"],
                  [ExternalLink, "Open it in Google Slides", <>Go to <b>Google&nbsp;Slides</b> → <b>File → Import slides</b> (or open Google Drive and upload it). Everyone has a Google account — no Apple or Microsoft needed.</>, "#2563eb"],
                  [Pencil, "Make it yours", "Edit the text, swap photos, adjust colours — polish it however you like.", "#7c3aed"],
                  [FileDown, "Download the slides", <>In Google Slides: <b>File → Download → the .pptx option</b>.</>, "#16a34a"],
                  [Check, "Open it anywhere", "That fresh file opens beautifully in Apple Keynote and Google Slides. Done!", "#4f7746"],
                ].map(([Ico, title, desc, color], i) => (
                  <li key={i} className="flex items-start gap-3" data-testid={`pptx-guide-step-${i + 1}`}>
                    <span className="relative shrink-0 mt-0.5">
                      <span className="w-8 h-8 rounded-full flex items-center justify-center text-white font-extrabold text-sm" style={{ background: color }}>{i + 1}</span>
                    </span>
                    <div className="pt-0.5">
                      <div className="flex items-center gap-1.5 font-bold text-sm text-[#2c2841]">
                        <Ico size={15} style={{ color }} /> {title}
                      </div>
                      <div className="text-[13px] text-[#a8a4ba] leading-snug mt-0.5">{desc}</div>
                    </div>
                  </li>
                ))}
              </ol>

              <div className="mt-5 flex flex-col gap-2">
                <button
                  data-testid="pptx-guide-download-btn"
                  onClick={downloadPptx}
                  disabled={pptxBusy}
                  className="w-full inline-flex items-center justify-center gap-2 font-extrabold text-white py-3 rounded-2xl transition-colors disabled:opacity-70"
                  style={{ background: "linear-gradient(135deg,#9a3412,#c2410c)" }}
                >
                  {pptxBusy ? <><Loader2 size={18} className="spin" /> Preparing…</> : <><Download size={18} /> Download Presentation</>}
                </button>
                <a
                  data-testid="pptx-guide-slides-link"
                  href="https://slides.google.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full inline-flex items-center justify-center gap-2 font-bold text-[#2563eb] bg-[#eef4ff] border-2 border-[#c9dbff] py-2.5 rounded-2xl hover:bg-[#e2edff] transition-colors"
                >
                  <ExternalLink size={16} /> Open Google Slides
                </a>
              </div>
              <p className="text-center text-[11px] text-[#b3aec6] mt-3 leading-snug">
                Tip: keep this window open while you work — your steps stay right here for reference.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="sms-card overflow-hidden sms-fade-up">
        <div className="flex flex-col">
          {/* Hero: full-width 16:9 landscape (matches the community hero) */}
          <div className="w-full flex flex-col">
            <div className="relative bg-[#f1eff8]">
              {(manual.mode === "minute" || manual.orientation === "portrait") ? (
                <div className="w-full bg-black flex items-center justify-center py-2" data-testid="minute-hero-portrait">
                  <div className="aspect-[9/16] h-[64vh] max-h-[560px] overflow-hidden bg-black">
                    {(manual.completion_image_id || manual.hero_image_id) ? (
                      <img src={imageUrl(manual.completion_image_id || manual.hero_image_id)} alt="" className="w-full h-full object-cover" />
                    ) : null}
                  </div>
                </div>
              ) : (
                <div className="w-full aspect-video overflow-hidden">
                  {manual.completion_image_id ? (
                    <img src={imageUrl(manual.completion_image_id)} alt="" className="w-full h-full object-cover" style={{ objectPosition: "center 30%" }} />
                  ) : manual.hero_image_id ? (
                    <img src={imageUrl(manual.hero_image_id)} alt="" className="w-full h-full object-cover" style={{ objectPosition: "center 30%" }} />
                  ) : null}
                </div>
              )}
              {(manual.completion_image_id || manual.hero_image_id) && (
                <div className="absolute bottom-3 right-3 flex items-center gap-2">
                  <button
                    data-testid="toggle-all-steps-btn"
                    onClick={toggleAllSteps}
                    title={anyStepOpen ? "Collapse all steps" : "Open all steps"}
                    aria-label={anyStepOpen ? "Collapse all steps" : "Open all steps"}
                    className="w-10 h-10 rounded-full bg-black/45 hover:bg-black/70 text-white flex items-center justify-center backdrop-blur-sm transition-colors"
                  >
                    {anyStepOpen ? <ChevronsDownUp size={18} /> : <ChevronsUpDown size={18} />}
                  </button>
                  <button
                    data-testid="copy-video-link-btn"
                    onClick={copyPageLink}
                    title="Copy a direct link to this video"
                    aria-label="Copy link to this video"
                    className="w-10 h-10 rounded-full bg-black/45 hover:bg-black/70 text-white flex items-center justify-center backdrop-blur-sm transition-colors"
                  >
                    <Copy size={18} />
                  </button>
                  <button
                    data-testid="hero-zoom-btn"
                    onClick={() => setHeroZoom(true)}
                    title="View full size"
                    aria-label="View full size"
                    className="w-10 h-10 rounded-full bg-black/45 hover:bg-black/70 text-white flex items-center justify-center backdrop-blur-sm transition-colors"
                  >
                    <ZoomIn size={18} />
                  </button>
                  <button
                    data-testid="hero-download-btn"
                    onClick={downloadHero}
                    disabled={heroDl}
                    title="Download this image"
                    aria-label="Download this image"
                    className="w-10 h-10 rounded-full bg-black/45 hover:bg-black/70 text-white flex items-center justify-center backdrop-blur-sm transition-colors disabled:opacity-60"
                  >
                    {heroDl ? <Loader2 size={18} className="spin" /> : <Download size={18} />}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Steps gallery — full width, directly below the hero */}
          <div className="p-5 sm:p-6 pt-4">
            {/* STEP FIVE label — full-width blue stripe at the TOP of the card, below the hero, above the gallery */}
            {manual.steps?.length > 0 && !(manual.moviestar && !manual.moviestar_step_photos) && (
              <div className="sms-step-head relative cursor-pointer select-none -mx-5 sm:-mx-6 -mt-4 sm:-mt-6 mb-4 py-2.5 text-center" data-collapsed={!stepOpen("s5")} onClick={() => toggleStep("s5")} style={{ background: "#1e3aaa" }} data-testid="step-three-title-wrap">
                <StepTitle testid="step-three-title" left="STEP FIVE" right="PREVIEW BEFORE PUBLISH" />
                <StepToggle k="s5" />
              </div>
            )}
            {manual.steps?.length > 0 && !(manual.moviestar && !manual.moviestar_step_photos) && (
              <div className="grid grid-cols-5 sm:grid-cols-8 gap-2" data-testid="preview-before-publish">
                {manual.steps.map((s, i) => {
                  const busy = !!regenSet[i];
                  const verdict = scanResults[i];
                  return (
                    <div key={i} className="flex flex-col items-center" data-testid={`preview-step-${i}`}>
                      <button
                        type="button"
                        onClick={() => { if (s.thumbnail_id) { setFixText(""); setFixZoom(""); setInsistFix(false); setPreviewImg({ id: s.thumbnail_id, index: i }); } }}
                        data-testid={`preview-open-${i}`}
                        className={`relative w-full aspect-square rounded-lg overflow-hidden bg-[#f1eff8] border-2 transition-colors cursor-zoom-in ${verdict && !verdict.ok ? "border-[#f59e0b]" : verdict && verdict.ok ? "border-[#16a34a]" : "border-[#e3e0ee] hover:border-[#c4b3f5]"}`}
                      >
                        {s.thumbnail_id ? (
                          <StepThumbImg src={`${imageUrl(s.thumbnail_id)}${imgBust ? `?v=${imgBust}` : ""}`} alt={`Step ${i + 1}`} busy={busy} />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-[#b3aec6]">
                            <Loader2 size={16} className="spin" />
                          </div>
                        )}
                        <span className="absolute top-0.5 left-0.5 text-[8px] font-bold text-white bg-black/55 rounded px-1 leading-tight">{i + 1}</span>
                        {verdict && (
                          <span
                            className={`absolute top-0.5 right-0.5 w-4 h-4 rounded-full flex items-center justify-center text-white ${verdict.ok ? "bg-[#16a34a]" : "bg-[#f59e0b]"}`}
                            title={verdict.ok ? "Looks good" : verdict.reason}
                            data-testid={`qa-badge-${i}`}
                          >
                            {verdict.ok ? <Check size={10} strokeWidth={3} /> : <AlertTriangle size={10} strokeWidth={3} />}
                          </span>
                        )}
                        {s.clip && (
                          <span className="absolute bottom-0.5 left-0.5 inline-flex items-center gap-0.5 text-[7px] font-extrabold text-black bg-[#f0c975] rounded px-1 py-0.5 leading-none" data-testid={`clip-badge-${i}`}>
                            <Film size={8} /> VIDEO
                          </span>
                        )}
                        {s.redone && (
                          <span
                            className="absolute bottom-0.5 right-0.5 text-[7px] font-extrabold text-white bg-[#4f7746] rounded px-1 py-0.5 leading-none"
                            title="You redid this photo — tap to switch between Original and New"
                            data-testid={`redone-badge-${i}`}
                          >
                            REDONE
                          </span>
                        )}
                        {busy && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <Loader2 size={18} className="spin text-[#4f7746]" />
                          </div>
                        )}
                      </button>
                      <button
                        data-testid={`regen-step-${i}`}
                        onClick={() => { if (s.thumbnail_id) { setFixText(""); setFixZoom(""); setInsistFix(false); setPreviewImg({ id: s.thumbnail_id, index: i }); } }}
                        disabled={busy || !s.thumbnail_id}
                        title="Inspect & fix this photo"
                        aria-label={`Inspect and fix step ${i + 1} photo`}
                        className={`-mt-3 z-10 w-7 h-7 rounded-full border-2 flex items-center justify-center shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${s.thumbnail_id && !busy ? "bg-[#1e3aaa] border-[#1e3aaa] text-white hover:bg-[#18308c] hover:border-[#18308c]" : "bg-[#171622] border-[#5d8a52] text-[#4f7746] hover:bg-[#eef3ea] hover:border-[#c4b3f5]"} ${manual.diy ? "hidden" : ""}`}
                      >
                        <RotateCcw size={13} className={busy ? "spin" : ""} />
                      </button>
                      {s.clip && (
                        <button type="button" data-testid={`clip-edit-${i}`} onClick={() => setClipEditIdx(i)} title="Swap, re-trim or remove this clip"
                          className="-mt-3 z-10 w-7 h-7 rounded-full border-2 border-[#f0c975] bg-[#171622] text-[#f0c975] flex items-center justify-center shadow-sm hover:bg-[#f0c975] hover:text-black transition-colors">
                          <Film size={13} />
                        </button>
                      )}
                      {manual.diy && (s.user_voice_id || (s.clip?.keep_sound && s.clip?.has_audio)) && (
                        <button type="button" data-testid={`cc-edit-${i}`} onClick={() => setCcEditIdx(i)} title="Review & fix this step's auto-captions"
                          className="mt-1 z-10 px-1.5 h-6 rounded-md border-2 border-white bg-[#171622] text-white text-[9px] font-extrabold flex items-center justify-center shadow-sm hover:bg-white hover:text-black transition-colors">
                          CC
                        </button>
                      )}
                    </div>
                  );
                })}
                <CaptionEditModal manualId={id} index={ccEditIdx} onClose={() => setCcEditIdx(null)} />
                <StepClipEditor manualId={id} index={clipEditIdx} clip={clipEditIdx != null ? manual?.steps?.[clipEditIdx]?.clip : null}
                  onClose={() => setClipEditIdx(null)}
                  onChanged={async () => { try { const { data } = await api.get(`/manuals/${id}`); setManual(data); setImgBust(Date.now()); } catch { /* keep */ } }} />
                {/* 16th spot — one-tap image reloader */}
                <div className="flex flex-col items-center">
                  <button
                    type="button"
                    data-testid="reload-images-tile"
                    onClick={reloadImages}
                    disabled={reloadingImgs}
                    title="Reload the step photos"
                    aria-label="Reload images"
                    className="relative w-full aspect-square rounded-lg overflow-hidden border-2 border-dashed border-[#5d8a52] bg-[#101019] flex flex-col items-center justify-center gap-1 text-[#a9d79b] hover:bg-[#16241a] hover:border-[#7ba46f] transition-colors disabled:opacity-60"
                  >
                    {reloadingImgs ? <Loader2 size={20} className="spin" /> : <RefreshCw size={20} />}
                    <span className="text-[9px] font-extrabold leading-[1.05] text-center px-0.5">Reload<br />Images</span>
                  </button>
                </div>
              </div>
            )}
            {/* Reload-images helper subtext — sits right beneath the gallery / reload tile */}
            {manual.steps?.length > 0 && !(manual.moviestar && !manual.moviestar_step_photos) && (
              <p className="text-center text-[11px] text-[#9a96ac] mt-2 leading-snug" data-testid="reload-images-hint">
                If an image takes too long to load, tap <b className="text-[#a9d79b]">Reload&nbsp;Images</b> above — it re-fetches any that didn't appear, no need to rebuild.
              </p>
            )}
            {/* DIY Camera Movement — add/change a smooth glide per photo without rebuilding the manual. */}
            {manual.diy && manual.steps?.length > 0 && (
              <div className="mt-4 sms-tweed rounded-2xl border-2 border-[#5d8a52] p-4" data-testid="diy-motion-editor">
                <div className="flex items-center gap-2">
                  <Film size={18} className="text-[#a9d79b]" />
                  <h4 className="text-white font-extrabold">Camera Movement <span className="text-white/45 text-sm font-bold">· optional</span></h4>
                </div>
                <p className="text-[12px] text-[#a8a4ba] mt-1 mb-3 leading-relaxed">
                  Give any photo a smooth glide — the text stays pinned in place. Changes apply the next time you make your video, no rebuild needed.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {manual.steps.map((s, i) => (
                    <div key={i} className="flex items-center gap-2.5 rounded-xl bg-black/30 border border-[#2a3a26] p-2" data-testid={`diy-motion-row-${i}`}>
                      <div className="shrink-0 w-12 h-12 rounded-lg overflow-hidden bg-[#0a0a0f] border border-white/10 flex items-center justify-center">
                        {s.thumbnail_id ? (
                          <img src={`${imageUrl(s.thumbnail_id)}${imgBust ? `?v=${imgBust}` : ""}`} alt={`Step ${i + 1}`} className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-[10px] font-bold text-white/40">{i + 1}</span>
                        )}
                      </div>
                      <span className="shrink-0 w-5 text-center text-xs font-extrabold text-white/60">{i + 1}</span>
                      <select
                        data-testid={`diy-motion-select-${i}`}
                        value={s.motion || "none"}
                        onChange={(e) => setStepMotion(i, e.target.value)}
                        title="Camera movement on this photo (text stays pinned)"
                        className="sms-input flex-1 min-w-0 px-2 py-1.5 text-xs font-bold text-white bg-black cursor-pointer"
                      >
                        <option value="none">Move · None</option>
                        <option value="push_in">Move · Push In</option>
                        <option value="reveal">Move · Reveal</option>
                        <option value="dolly_left">Move · Dolly L</option>
                        <option value="dolly_right">Move · Dolly R</option>
                        <option value="auto">Move · Auto ✨</option>
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* Preview Before Publish — centered title + explainer, directly under the gallery */}
            {manual.steps?.length > 0 && !(manual.moviestar && !manual.moviestar_step_photos) && (
              <div className="mt-2 text-center" data-testid="preview-before-publish-info">
                <div className="flex items-center justify-center gap-2 text-2xl font-extrabold text-white">
                  <Images size={24} style={{ color: "#5d8a52" }} /> Preview Before Publish
                </div>
                <p className="text-[13px] text-[#a8a4ba] mt-1.5 leading-relaxed">
                  {manual.diy
                    ? <>These are the photos you uploaded — one for each step. Tap any thumbnail to take a closer look before you make your video.</>
                    : <>Tap any thumbnail above to check each photo for mistakes. Spot one? Tap <RotateCcw size={12} className="inline -mt-0.5" /> under it, and in the popup window <b>describe the fix in a few words</b> — we'll regenerate a fixed version of that same shot, <b>before</b> you make your video. You get <b>{manual.redo_limit ?? 2} free redos per photo</b>, and your originals are always saved so you can flip back.</>}
                </p>
              </div>
            )}
            {/* Check my photos — moved ABOVE the All Done headline */}
            {manual.steps?.length > 0 && !manual.diy && !(manual.moviestar && !manual.moviestar_step_photos) && (
              <div className="flex flex-col items-center mt-6" data-testid="scan-photos-section">
                <button
                  data-testid="scan-photos-btn"
                  onClick={scanPhotos}
                  disabled={scanning}
                  className="inline-flex items-center gap-2 text-[13px] font-bold text-white bg-[#dc2626] border-2 border-[#b91c1c] rounded-full px-5 py-2 hover:bg-[#b91c1c] transition-colors disabled:opacity-60"
                >
                  {scanning ? <Loader2 size={15} className="spin" /> : <ScanSearch size={15} />}
                  {scanning ? "Scanning your photos…" : "Check my photos for problems"}
                </button>
                {Object.keys(scanResults).length > 0 && !scanning && (
                  <p className="text-[11px] text-[#9a96ac] mt-1.5" data-testid="scan-summary">
                    {Object.values(scanResults).filter((r) => !r.ok).length === 0
                      ? "✓ All photos passed — no obvious problems found."
                      : `⚠ ${Object.values(scanResults).filter((r) => !r.ok).length} photo(s) flagged. Look for the amber badge.`}
                  </p>
                )}
              </div>
            )}
            {/* Post-hoc restyle removed — the look (host, no-people, day/night, indoor/outdoor, scene) is now chosen up front on the create form, so photos generate only once. */}
            {false && (
              <div className="mt-4 rounded-2xl border-2 border-[#5d8a52] bg-[#f7faf5] p-3 max-w-[420px] mx-auto" data-testid="two-people-card">
                <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="two-people-toggle">
                  <input
                    type="checkbox"
                    checked={twoPeople}
                    onChange={(e) => { setTwoPeople(e.target.checked); if (e.target.checked) setNoPeople(false); }}
                    disabled={twoBusy}
                    className="w-4 h-4 rounded accent-[#5d8a52]"
                  />
                  <span className="text-sm font-semibold text-[#4a475c] inline-flex items-center gap-1">
                    <Users size={15} className="text-[#5d8a52]" /> Feature two people in every photo
                  </span>
                </label>
                {twoPeople && !noPeople && (
                  <div className="mt-2">
                    <input
                      data-testid="cast-hint-input"
                      value={castHint}
                      onChange={(e) => setCastHint(e.target.value)}
                      maxLength={200}
                      disabled={twoBusy}
                      placeholder='Who are they? e.g. "mother + 10-year-old daughter"'
                      className="sms-input w-full px-3 py-2 text-sm"
                      style={{ border: "2px solid #5d8a52" }}
                    />
                    <p className="text-[11px] text-[#9a96ac] mt-1.5 leading-relaxed">
                      Locks both people — same faces, ages, hair &amp; outfits in every shot. This regenerates your step photos (keeps a consistent look, not pixel-identical real faces).
                    </p>
                  </div>
                )}
                <label className="flex items-center gap-2 cursor-pointer select-none mt-3 pt-3 border-t border-[#dbe4d3]" data-testid="no-people-toggle">
                  <input
                    type="checkbox"
                    checked={noPeople}
                    onChange={(e) => { setNoPeople(e.target.checked); if (e.target.checked) setTwoPeople(false); }}
                    disabled={twoBusy}
                    className="w-4 h-4 rounded accent-[#5d8a52]"
                  />
                  <span className="text-sm font-semibold text-[#4a475c] inline-flex items-center gap-1">
                    <EyeOff size={15} className="text-[#5d8a52]" /> No people in this video
                  </span>
                </label>
                {noPeople && (
                  <p className="text-[11px] text-[#9a96ac] mt-1.5 leading-relaxed">
                    Just the subject — no people, faces or hands. Clean top-down or first-person POV shots (like photos snapped through smart glasses). Regenerates your step photos.
                  </p>
                )}
                {(twoPeople !== !!manual.two_people || noPeople !== !!manual.no_people || (twoPeople && castHint.trim() !== (manual.cast_hint || ""))) && (
                  <button
                    data-testid="apply-two-people-btn"
                    onClick={applyPeople}
                    disabled={twoBusy}
                    className="mt-3 w-full inline-flex items-center justify-center gap-2 font-bold py-2.5 rounded-xl text-white text-sm transition-colors disabled:opacity-50"
                    style={{ background: "linear-gradient(135deg,#5d8a52,#3f6238)" }}
                  >
                    {twoBusy ? <><Loader2 size={15} className="spin" /> Regenerating photos…</> : <><Wand2 size={15} /> Apply — regenerate photos</>}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* CARD 2 — STEP SIX: Download digital assets & quick actions */}
      <div className="rounded-2xl border-2 mt-4 p-5 sm:p-6 text-center sms-fade-up" style={{ borderColor: "#5d8a52", background: "#0f0f16 repeating-linear-gradient(45deg, rgba(93,138,82,.06) 0px, rgba(93,138,82,.06) 10px, transparent 10px, transparent 20px)" }} data-testid="step-four-card">
          {/* STEP SIX header — full-width blue stripe across the top of the black card */}
          <div className="sms-step-head relative cursor-pointer select-none -mx-5 sm:-mx-6 -mt-5 sm:-mt-6 mb-4 py-2.5 text-center rounded-t-2xl" data-collapsed={!stepOpen("s6")} onClick={() => toggleStep("s6")} style={{ background: "#1e3aaa" }} data-testid="step-four-title-wrap">
            <StepTitle testid="step-four-title" left="STEP SIX" right="DIGITAL DOWNLOADS" />
            <StepToggle k="s6" />
          </div>
          <div className="mb-3 flex items-center justify-center gap-2 text-2xl font-extrabold text-white" data-testid="download-assets-title">
            <Download size={24} style={{ color: "#5d8a52" }} /> Download Digital Assets
          </div>
          {/* The six individual asset downloads — uniform Levi Blue, white lettering. */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
            {manual.steps?.length > 0 && (
              <button data-testid="download-stills-btn" onClick={downloadStills} disabled={stillsDl}
                className="flex flex-col items-center justify-center gap-1 text-[11px] font-bold text-white border-2 rounded-xl py-2.5 px-1 leading-tight transition-colors disabled:opacity-70 hover:brightness-110"
                style={{ background: "#1e3aaa", borderColor: "#16308c" }}>
                {stillsDl ? <Loader2 size={17} className="spin" /> : <Images size={17} />}
                <span>{stillsDl ? "Preparing…" : "Step Pics"}</span>
              </button>
            )}
            <button data-testid="amazon-links-btn" onClick={downloadAmazonLinks} disabled={amazonBusy}
              title="Download a sheet of Amazon affiliate links for the recommended supplies"
              className="flex flex-col items-center justify-center gap-1 text-[11px] font-bold text-white border-2 rounded-xl py-2.5 px-1 leading-tight transition-colors disabled:opacity-70 hover:brightness-110"
              style={{ background: "#1e3aaa", borderColor: "#16308c" }}>
              {amazonBusy ? <Loader2 size={17} className="spin" /> : <ShoppingCart size={17} />} <span>Amazon</span>
            </button>
            <button data-testid="pdf-btn" onClick={() => downloadPdfLang(videoLang)} disabled={!!pdfLang}
              title={`Download this guide as a PDF in ${LANGS[videoLang]?.native || "English"}`}
              className="flex flex-col items-center justify-center gap-0.5 text-[11px] font-bold text-white border-2 rounded-xl py-2 px-1 leading-tight transition-colors disabled:opacity-70 hover:brightness-110"
              style={{ background: "#1e3aaa", borderColor: "#16308c" }}>
              {pdfLang === videoLang ? <Loader2 size={17} className="spin" /> : <FileDown size={17} />}
              <span>PDF</span>
              <span className="text-[10px] font-semibold text-white/80" data-testid="pdf-btn-lang">{LANGS[videoLang]?.native || "English"}</span>
            </button>
            <button data-testid="pptx-btn" onClick={() => setShowPptxGuide(true)}
              title="Editable slideshow — opens in Keynote, Google Slides & any slideshow app"
              className="flex flex-col items-center justify-center gap-0.5 text-[11px] font-bold text-white border-2 rounded-xl py-2 px-1 leading-tight transition-colors disabled:opacity-70 hover:brightness-110"
              style={{ background: "#1e3aaa", borderColor: "#16308c" }}>
              <MonitorPlay size={17} />
              <span>Slides</span>
              <span className="text-[10px] font-semibold text-white/80" data-testid="pptx-btn-label">Keynote</span>
            </button>
            <button data-testid="handout-btn" onClick={downloadHandout} disabled={handoutBusy}
              title="Download a printable one-page teacher worksheet (steps, note lines & discussion questions)"
              className="flex flex-col items-center justify-center gap-0.5 text-[11px] font-bold text-white border-2 rounded-xl py-2 px-1 leading-tight transition-colors disabled:opacity-70 hover:brightness-110"
              style={{ background: "#1e3aaa", borderColor: "#16308c" }}>
              {handoutBusy ? <Loader2 size={17} className="spin" /> : <ClipboardList size={17} />}
              <span>Handout</span>
              <span className="text-[10px] font-semibold text-white/80" data-testid="handout-btn-label">Worksheet</span>
            </button>
            <button data-testid="answer-key-btn" onClick={downloadAnswerKey} disabled={answerKeyBusy}
              title="Download the completed teacher Answer Key (step explanations + suggested discussion answers)"
              className="flex flex-col items-center justify-center gap-0.5 text-[11px] font-bold text-white border-2 rounded-xl py-2 px-1 leading-tight transition-colors disabled:opacity-70 hover:brightness-110"
              style={{ background: "#1e3aaa", borderColor: "#16308c" }}>
              {answerKeyBusy ? <Loader2 size={17} className="spin" /> : <KeyRound size={17} />}
              <span>Answer Key</span>
              <span className="text-[10px] font-semibold text-white/80" data-testid="answer-key-btn-label">Teacher</span>
            </button>
          </div>

          {/* Choose the download language — centered, under the download buttons. */}
          <div className="mt-4 mb-4 flex items-center justify-center flex-wrap gap-1.5" data-testid="pdf-lang-row">
            <span className="text-xs font-bold text-[#4f7746] flex items-center gap-1 mr-0.5">
              <FileDown size={13} /> PDF language:
            </span>
            {PDF_LANGS.map(({ code, label }) => (
              <button
                key={code}
                data-testid={`pdf-lang-${code}`}
                onClick={() => downloadPdfLang(code)}
                disabled={!!pdfLang}
                title={`Download this guide as a PDF in ${label}`}
                className={`min-w-[40px] text-xs font-extrabold px-2.5 py-1 rounded-full border-2 border-[#4f7746] transition-colors disabled:opacity-50 flex items-center justify-center gap-1 ${(manual.language || "en").toLowerCase() === code ? "bg-[#4f7746] text-white" : "bg-[#171622] text-[#4f7746] hover:bg-[#eef3ea]"}`}
              >
                {pdfLang === code ? <Loader2 size={12} className="spin" /> : label}
              </button>
            ))}
          </div>

          {/* Quick actions — Print · Share (Replay moved into the gallery's 16th spot as "Reload Images"). */}
          <div className="grid grid-cols-2 gap-2 max-w-md mx-auto mb-5" data-testid="quick-actions-row">
            <button data-testid="print-btn" onClick={printManual}
              className="flex flex-col items-center justify-center gap-1 text-xs font-bold text-[#4a475c] bg-[#171622] border-2 border-[#4f7746] rounded-xl py-2.5 px-1 hover:bg-[#faf9fd] transition-colors">
              <Printer size={17} /> <span>Print</span>
            </button>
            <button data-testid="share-btn" onClick={share}
              className="flex flex-col items-center justify-center gap-1 text-xs font-bold text-[#4a475c] bg-[#171622] border-2 border-[#4f7746] rounded-xl py-2.5 px-1 hover:bg-[#faf9fd] transition-colors">
              <Share2 size={17} /> <span>Share</span>
            </button>
          </div>

          <div className="max-w-md mx-auto" data-testid="download-all-section">
            {!dlAll ? (
              assetsDownloaded ? (
                <button
                  data-testid="download-all-redownload"
                  onClick={downloadAllAssets}
                  className="w-full flex items-center justify-center gap-2 text-[13px] font-extrabold text-white rounded-full py-2.5 px-4 transition-transform hover:scale-[1.01]"
                  style={{ background: "#dc2626", border: "2px solid #b91c1c", boxShadow: "0 10px 22px -12px rgba(220,38,38,.85)" }}
                >
                  <Check size={16} className="shrink-0" /> <span>Downloaded · click to re-download</span>
                </button>
              ) : (
                <button
                  data-testid="download-all-btn"
                  onClick={downloadAllAssets}
                  className="w-full flex items-center justify-center gap-2 text-[13px] font-extrabold text-white rounded-full py-2.5 px-4 transition-transform hover:scale-[1.01]"
                  style={{ background: "#dc2626", border: "2px solid #b91c1c", boxShadow: "0 10px 22px -12px rgba(220,38,38,.85)" }}
                >
                  <Download size={16} /> Download All
                </button>
              )
            ) : dlAll.done ? (
              <div
                className="w-full flex items-center justify-center gap-2 text-[13px] font-extrabold text-white rounded-full py-2.5 px-4 text-center"
                style={{ background: "#dc2626", border: "2px solid #b91c1c", boxShadow: "0 10px 22px -12px rgba(220,38,38,.85)" }}
                data-testid="download-all-done"
              >
                <Check size={16} className="shrink-0" /> <span>Zip downloaded — check your downloads folder</span>
              </div>
            ) : (
              <div className="rounded-full border-2 px-4 py-2.5 pe-render-fill" style={{ borderColor: "#c79a05" }} data-testid="download-all-progress">
                <div className="flex items-center justify-between text-[13px] font-extrabold text-black">
                  <span className="flex items-center gap-1.5 truncate">
                    <Loader2 size={14} className="spin" /> {dlAll.label}
                  </span>
                  <span className="shrink-0 tabular-nums">{dlAll.mb.toFixed(1)} MB downloaded</span>
                </div>
              </div>
            )}
            {dlAll && !dlAll.done && (
              <p data-testid="download-all-wait-note" className="text-[12px] font-bold text-[#ffd21e] text-center mt-2 px-2">
                Please stay on this step until your kit finishes downloading.
              </p>
            )}
          </div>
      </div>

      {/* Video area — STEP SEVEN (thumbnail) + STEP EIGHT (video). Each idle sub-section is its own card. */}
      <div data-testid="video-section">
            {videoState === "building" ? (
              queueInfo ? (
                <div className="rounded-2xl border-2 border-[#5d8a52] bg-[#faf8ff] mt-4 pt-5 pb-6 px-5 flex flex-col items-center text-center" data-testid="video-queued">
                  <div className="w-14 h-14 rounded-full bg-[#eef3ea] flex items-center justify-center mb-3">
                    <Timer size={28} style={{ color: "#4f7746" }} className="spin" />
                  </div>
                  <p className="font-extrabold text-lg text-white" data-testid="queue-headline">You're in line!</p>
                  {queueInfo.position ? (
                    <p className="text-sm font-semibold text-white mt-1" data-testid="queue-position">
                      Your spot: <span className="text-[#4f7746] font-extrabold text-base">#{queueInfo.position}</span>
                      {queueInfo.total > 1 ? <span className="text-[#9a96ac]"> of {queueInfo.total} waiting</span> : null}
                    </p>
                  ) : (
                    <p className="text-sm font-semibold text-white mt-1" data-testid="queue-position">Reserving your spot…</p>
                  )}
                  {queueInfo.position ? (() => {
                    const vals = renderTimes.map(secsOf).filter((n) => typeof n === "number" && n > 0);
                    const avg = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 180;
                    const etaMin = Math.max(1, Math.round((queueInfo.position * avg) / 2 / 60));
                    return (
                      <p className="text-xs font-bold text-[#4f7746] mt-1.5" data-testid="queue-eta">
                        About ~{etaMin} min until your turn
                      </p>
                    );
                  })() : null}
                  <p className="text-xs text-white mt-3 max-w-[320px] leading-relaxed">
                    Our video studio is busy right now — a great sign! Your render will start automatically the moment a slot opens. <b>Keep this page open</b> and you'll move up the line.
                  </p>
                  {cancelWindow && (
                    <button
                      data-testid="cancel-queue-btn"
                      onClick={cancelVideo}
                      className="mt-4 flex items-center gap-1.5 text-sm font-bold text-[#b4436c] bg-[#171622] border border-[#f0c9d6] rounded-full px-4 py-1.5 hover:bg-[#fdf3f6] transition-colors"
                    >
                      <X size={14} /> Leave the line
                    </button>
                  )}
                </div>
              ) : (
              <div className="sms-tweed rounded-2xl border-2 border-[#5d8a52] mt-4 pt-4 pb-6 px-4 flex flex-col items-center" data-testid="video-building">
                <p className="font-extrabold text-lg text-white">Creating Your Video…</p>
                <p className="text-sm font-semibold text-[#9a96ac] text-center italic transition-opacity duration-500 min-h-[20px] mb-3" data-testid="video-comfort">
                  {COMFORT_LINES[comfortIdx % COMFORT_LINES.length]}
                </p>
                <div className="flex items-start justify-center gap-4 w-full flex-wrap">
                  <RenderStopwatches times={renderTimes} live={liveElapsed} onDelete={deleteRenderTime} />
                  <div className="flex flex-col items-center">
                    <StaircaseLoader width={170} progress={progress} white red />
                    <p className="font-bold text-white mt-3 text-center">
                      {progress >= 94 ? "Almost There — Finishing Up…" : progress >= 60 ? "Rendering Your Video…" : progress >= 35 ? "Adding Narration…" : "Creating Your Step Photos…"}
                    </p>
                  </div>
                </div>
                <p className="text-xs text-white mt-3 text-center">Remain on this page during rendering, or it may be interrupted.</p>
                <p className="text-[11px] text-white mt-1.5 text-center max-w-[300px] leading-snug" data-testid="render-fineprint">
                  A wired connection renders faster than Wi-Fi. Be patient — your video's in the oven 🍞
                </p>
                <p className="text-[11px] font-semibold text-white mt-1.5 text-center max-w-[300px] leading-snug" data-testid="render-timeout-note">
                  If your video takes more than 8 minutes, rendering will restart automatically — no charge.
                </p>
                {cancelWindow && (
                  <button
                    data-testid="cancel-video-btn"
                    onClick={cancelVideo}
                    className="mt-4 flex items-center gap-1.5 text-sm font-bold text-[#b4436c] bg-[#171622] border border-[#f0c9d6] rounded-full px-4 py-1.5 hover:bg-[#fdf3f6] transition-colors"
                  >
                    <X size={14} /> Cancel — no charge
                  </button>
                )}
                <div className="mt-3 flex items-center justify-center gap-4 flex-wrap">
                  <button
                    data-testid="best-results-open"
                    onClick={() => setShowTips(true)}
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-[#5d8a52] hover:text-[#3f6238] transition-colors"
                  >
                    <Info size={13} /> For best results
                  </button>
                  {stalled && !cancelWindow && (
                    <button
                      data-testid="retry-video-btn"
                      onClick={retryVideo}
                      className="inline-flex items-center gap-1.5 text-xs font-bold text-[#5d8a52] hover:text-[#3f6238] transition-colors"
                    >
                      <RotateCcw size={13} /> Taking too long? Retry
                    </button>
                  )}
                </div>
              </div>
              )
            ) : (
              <>
                {/* CARD 3 — STEP SEVEN: Thumbnail Studio (persistent preview panel) */}
                <div className="rounded-2xl border-2 mt-4 p-5 sm:p-6 sms-fade-up" style={{ borderColor: "#5d8a52", background: "#0f0f16 repeating-linear-gradient(45deg, rgba(93,138,82,.06) 0px, rgba(93,138,82,.06) 10px, transparent 10px, transparent 20px)" }} data-testid="step-five-card">
                {/* STEP SEVEN header — full-width blue stripe across the top of the black card */}
                <div className="sms-step-head relative cursor-pointer select-none -mx-5 sm:-mx-6 -mt-5 sm:-mt-6 mb-4 py-2.5 text-center rounded-t-2xl" data-collapsed={!stepOpen("s7")} onClick={() => toggleStep("s7")} style={{ background: "#1e3aaa" }} data-testid="step-five-title-wrap">
                  <StepTitle testid="step-five-title" left="STEP SEVEN" right="THUMBNAIL" />
                  <StepToggle k="s7" />
                </div>
                <div className="mt-2 pt-2" data-testid="thumbnail-studio">
                        <div className="space-y-2.5">
                          <div className="flex items-center justify-center gap-2 text-2xl font-extrabold text-white text-center">
                            <Sparkles size={24} style={{ color: "#5d8a52" }} /> Thumbnail Studio
                          </div>
                          <p className="text-xs text-white leading-relaxed">
                            No extra image to generate — <span className="font-semibold">pick one of your photos below</span> and we'll lay your title right on it. Instant &amp; free.
                          </p>

                          {/* Numbered image picker — reuse one of the step photos you already have */}
                          <div data-testid="thumb-image-picker">
                            <label className="flex items-center gap-1.5 text-xs font-bold text-white mb-1.5 px-0.5">
                              <Images size={13} style={{ color: "#5d8a52" }} /> Select which image to use for your thumbnail
                            </label>
                            {(() => {
                              const srcPhotos = (manual.steps || []).filter((s) => s.thumbnail_id);
                              if (thumbExpanded && thumbSourceIdx != null && srcPhotos[thumbSourceIdx]) {
                                const cur = thumbSourceIdx;
                                const go = (dir) => {
                                  const n = (cur + dir + srcPhotos.length) % srcPhotos.length;
                                  setThumbSourceIdx(n); setThumbSourceId(srcPhotos[n].thumbnail_id); setThumbPreview(null); setThumbSaved(null);
                                };
                                return (
                                  <div className="space-y-2" data-testid="thumb-expanded-preview">
                                    <div className="relative rounded-xl overflow-hidden border-2 border-[#4f7746] bg-black" style={{ aspectRatio: "16 / 9" }}>
                                      <img src={imageUrl(srcPhotos[cur].thumbnail_id)} alt={`Photo ${cur + 1}`} className="w-full h-full object-cover" />
                                      <span className="absolute top-2 left-2 text-[11px] font-extrabold text-white bg-black/60 rounded px-1.5 py-0.5">Photo {cur + 1} of {srcPhotos.length}</span>
                                      {srcPhotos.length > 1 && (
                                        <>
                                          <button type="button" data-testid="thumb-prev-photo" onClick={() => go(-1)} className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center transition-colors"><ChevronLeft size={22} /></button>
                                          <button type="button" data-testid="thumb-next-photo" onClick={() => go(1)} className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center transition-colors"><ChevronRight size={22} /></button>
                                        </>
                                      )}
                                    </div>
                                    <button type="button" data-testid="thumb-gallery-return" onClick={() => setThumbExpanded(false)} className="w-full inline-flex items-center justify-center gap-1.5 text-sm font-bold text-[#4f7746] bg-[#eef3ea] hover:bg-[#e3ebdd] rounded-full py-2 transition-colors">
                                      <LayoutGrid size={15} /> Back to all photos
                                    </button>
                                  </div>
                                );
                              }
                              return (
                                <>
                                  <div className="grid grid-cols-8 gap-2">
                                    {srcPhotos.map((s, i) => {
                                      const active = thumbSourceIdx === i;
                                      return (
                                        <button
                                          key={s.thumbnail_id}
                                          type="button"
                                          data-testid={`thumb-source-${i}`}
                                          onClick={() => { setThumbSourceId(s.thumbnail_id); setThumbSourceIdx(i); setThumbExpanded(true); setThumbPreview(null); setThumbSaved(null); }}
                                          className="relative w-full rounded-lg overflow-hidden transition-transform hover:scale-105"
                                          style={{ aspectRatio: "16 / 10", border: active ? "3px solid #4f7746" : "2px solid #d8d2ea" }}
                                          title={`Tap to preview photo ${i + 1}`}
                                        >
                                          <img src={imageUrl(s.thumbnail_id)} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
                                          <span className="absolute top-0 left-0 text-[10px] font-extrabold text-white bg-black/60 rounded-br px-1 leading-tight">{i + 1}</span>
                                          {active && <span className="absolute inset-0 flex items-center justify-center bg-[#4f7746]/35"><Check size={16} className="text-white" /></span>}
                                        </button>
                                      );
                                    })}
                                  </div>
                                  <p className="text-[11px] text-white mt-1.5 px-0.5">Tap any photo to preview it larger, then pick just one.</p>
                                </>
                              );
                            })()}
                          </div>

                          <input
                            data-testid="thumbnail-title-input"
                            value={thumbTitle}
                            onChange={(e) => { setThumbTitle(e.target.value); setThumbPreview(null); setThumbSaved(null); }}
                            maxLength={120}
                            placeholder="Enter the title of your video here."
                            className="sms-input w-full px-4 py-2.5 text-sm"
                            style={{ border: "2px solid #5d8a52" }}
                          />
                          <p className="text-xs text-white -mt-1 leading-relaxed">
                            Create your title here — keep it simple and direct, e.g. <span className="font-semibold">"How To Make Grandma's Apple Pie"</span>.
                          </p>

                          {/* Title Font picker */}
                          <div>
                            <label className="flex items-center gap-1.5 text-xs font-bold text-white mb-1 px-0.5">
                              <Pencil size={13} style={{ color: "#5d8a52" }} /> Title Font
                            </label>
                            <select
                              data-testid="thumbnail-font-select"
                              value={thumbFont}
                              onChange={(e) => { setThumbFont(e.target.value); setThumbPreview(null); setThumbSaved(null); }}
                              className="w-full rounded-xl px-3 py-2.5 text-sm font-bold text-white bg-[#171622] cursor-pointer"
                              style={{ border: "2px solid #5d8a52" }}
                            >
                              {THUMB_FONTS.map((f) => (
                                <option key={f.key} value={f.key}>{f.label}</option>
                              ))}
                            </select>
                          </div>

                          {/* Title Color picker */}
                          <div data-testid="thumbnail-color-picker">
                            <label className="flex items-center gap-1.5 text-xs font-bold text-white mb-1.5 px-0.5">
                              <Sparkles size={13} style={{ color: "#5d8a52" }} /> Title Color
                            </label>
                            <div className="flex flex-wrap gap-2">
                              {THUMB_COLORS.map((c) => {
                                const active = thumbColor === c.value;
                                return (
                                  <button
                                    key={c.label}
                                    type="button"
                                    title={c.label}
                                    data-testid={`thumbnail-color-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                                    onClick={() => { setThumbColor(c.value); setThumbPreview(null); setThumbSaved(null); }}
                                    className="relative w-8 h-8 rounded-full transition-transform hover:scale-110"
                                    style={{
                                      background: c.value,
                                      border: active ? "3px solid #171426" : "2px solid #d8d2ea",
                                      boxShadow: active ? "0 0 0 2px #fff inset" : "none",
                                    }}
                                  >
                                    {active && <Check size={14} className="absolute inset-0 m-auto" style={{ color: (c.label === "White" || c.label === "Yellow" || c.label === "Gold") ? "#171426" : "#fff" }} />}
                                  </button>
                                );
                              })}
                              {THUMB_GRADIENTS.map((g) => {
                                const active = thumbColor === g.value;
                                return (
                                  <button
                                    key={g.value}
                                    type="button"
                                    title={g.label}
                                    data-testid={`thumbnail-color-${g.label.toLowerCase()}`}
                                    onClick={() => { setThumbColor(g.value); setThumbPreview(null); setThumbSaved(null); }}
                                    className="relative w-8 h-8 rounded-full transition-transform hover:scale-110"
                                    style={{ background: g.css, border: active ? "3px solid #171426" : "2px solid #d8d2ea" }}
                                  >
                                    {active && <Check size={14} className="absolute inset-0 m-auto text-white" />}
                                  </button>
                                );
                              })}
                              {/* Custom color */}
                              <label
                                data-testid="thumbnail-color-custom"
                                title="Custom color"
                                className="relative w-8 h-8 rounded-full cursor-pointer overflow-hidden flex items-center justify-center"
                                style={{
                                  background: customColor,
                                  border: thumbColor === "custom" ? "3px solid #171426" : "2px solid #d8d2ea",
                                }}
                              >
                                <Pencil size={12} style={{ color: "#fff", mixBlendMode: "difference" }} />
                                <input
                                  type="color"
                                  value={customColor}
                                  onChange={(e) => { setCustomColor(e.target.value); setThumbColor("custom"); setThumbPreview(null); setThumbSaved(null); }}
                                  className="absolute inset-0 opacity-0 cursor-pointer"
                                />
                              </label>
                            </div>
                            <p className="text-[11px] text-white mt-1.5 px-0.5">Pick a color — the last circle opens a custom color wheel.</p>
                          </div>

                          {/* Title Placement picker */}
                          <div>
                            <label className="flex items-center gap-1.5 text-xs font-bold text-white mb-1 px-0.5">
                              <MapPin size={13} style={{ color: "#5d8a52" }} /> Title Placement
                            </label>
                            <select
                              data-testid="thumbnail-placement-select"
                              value={thumbPlacement}
                              onChange={(e) => { setThumbPlacement(e.target.value); setThumbPreview(null); setThumbSaved(null); }}
                              className="w-full rounded-xl px-3 py-2.5 text-sm font-bold text-white bg-[#171622] cursor-pointer"
                              style={{ border: "2px solid #5d8a52" }}
                            >
                              {THUMB_PLACEMENTS.map((pl) => (
                                <option key={pl.key} value={pl.key}>{pl.label}</option>
                              ))}
                            </select>
                            <p className="text-[11px] text-white mt-1 px-0.5">Where your title sits on the photo — top, middle, bottom, or left/right.</p>
                          </div>

                          {/* Font Outline — always open, swatch picker like the color selector; pick "Off" for none */}
                          <div data-testid="thumb-outline-block">
                            <label className="flex items-center gap-1.5 text-xs font-bold text-white mb-1 px-0.5">
                              <Pencil size={13} style={{ color: "#5d8a52" }} /> Font Outline
                            </label>
                            <p className="text-[11px] text-white mb-1.5 px-0.5">Adds a bold outline around every letter so the title pops off busy photos. Pick <b>Off</b> for no outline.</p>
                            <div className="flex flex-wrap gap-2">
                              <button
                                type="button"
                                data-testid="thumb-outline-none"
                                title="No outline"
                                onClick={() => { setThumbOutline(false); setThumbPreview(null); setThumbSaved(null); }}
                                className="relative w-8 h-8 rounded-full flex items-center justify-center transition-transform hover:scale-110"
                                style={{ background: "#0a0a0f", border: !thumbOutline ? "3px solid #171426" : "2px solid #d8d2ea" }}
                              >
                                <span className="text-[10px] font-bold" style={{ color: "#cfe0c6" }}>Off</span>
                              </button>
                              {THUMB_COLORS.map((c) => {
                                const active = thumbOutline && thumbOutlineColor === c.value;
                                return (
                                  <button
                                    key={c.value}
                                    type="button"
                                    data-testid={`thumb-outline-color-${c.label.toLowerCase()}`}
                                    title={c.label}
                                    onClick={() => { setThumbOutline(true); setThumbOutlineColor(c.value); setThumbPreview(null); setThumbSaved(null); }}
                                    className="relative w-8 h-8 rounded-full transition-transform hover:scale-110"
                                    style={{ background: c.value, border: active ? "3px solid #171426" : "2px solid #d8d2ea" }}
                                  >
                                    {active && <Check size={14} className="absolute inset-0 m-auto" style={{ color: (c.label === "White" || c.label === "Yellow" || c.label === "Gold") ? "#171426" : "#fff" }} />}
                                  </button>
                                );
                              })}
                              <label
                                data-testid="thumb-outline-color-custom"
                                title="Custom outline color"
                                className="relative w-8 h-8 rounded-full cursor-pointer overflow-hidden flex items-center justify-center"
                                style={{ background: thumbOutlineCustom, border: (thumbOutline && thumbOutlineColor === "custom") ? "3px solid #171426" : "2px solid #d8d2ea" }}
                              >
                                <Pencil size={12} style={{ color: "#fff", mixBlendMode: "difference" }} />
                                <input type="color" value={thumbOutlineCustom} onChange={(e) => { setThumbOutlineCustom(e.target.value); setThumbOutlineColor("custom"); setThumbOutline(true); setThumbPreview(null); setThumbSaved(null); }} className="absolute inset-0 opacity-0 cursor-pointer" />
                              </label>
                            </div>
                          </div>

                          {/* Nudge Subject — one control: tap an arrow to slide the subject that way (tap again to re-center) */}
                          <div data-testid="thumb-shift-block">
                            <label className="flex items-center gap-1.5 text-xs font-bold text-white mb-1 px-0.5">
                              <MoveHorizontal size={13} style={{ color: "#5d8a52" }} /> Nudge Subject
                            </label>
                            <p className="text-[11px] text-white mb-3 px-0.5">Putting your title on the <b>Left or Right</b>? Tap an arrow to slide your subject the opposite way and open clean space for the text — their face stays in frame. Tap the lit arrow again to re-center.</p>
                            <div className="relative mx-auto my-1" style={{ maxWidth: 300, aspectRatio: "16 / 9" }} data-testid="thumb-nudge-control">
                              <img
                                src="https://static.prod-images.emergentagent.com/jobs/2f7bdabb-c81c-4f41-9fe2-245fc40e5116/images/b2e009cf89cf3e3560923abdb1410e0b74a3b44ea8668cc398d2072c3c3286a2.jpeg"
                                alt="Nudge control — tap an arrow to shift the subject that direction"
                                className="w-full h-full object-cover rounded-xl border border-[#2a2536]"
                              />
                              {[
                                ["up", "▲", "left-1/2 -translate-x-1/2 top-1.5"],
                                ["down", "▼", "left-1/2 -translate-x-1/2 bottom-1.5"],
                                ["left", "◀", "top-1/2 -translate-y-1/2 left-1.5"],
                                ["right", "▶", "top-1/2 -translate-y-1/2 right-1.5"],
                              ].map(([val, ar, pos]) => {
                                const active = thumbShift === val;
                                return (
                                  <button
                                    key={val}
                                    type="button"
                                    data-testid={`thumb-shift-${val}`}
                                    title={`Nudge subject ${val}`}
                                    onClick={() => { setThumbShift(active ? "" : val); setThumbPreview(null); setThumbSaved(null); }}
                                    className={`absolute ${pos} w-9 h-9 rounded-full flex items-center justify-center text-sm shadow-lg transition-transform hover:scale-110`}
                                    style={active
                                      ? { background: "#facc15", color: "#171426", border: "2px solid #fff" }
                                      : { background: "#4f7746", color: "#fff", border: "2px solid #a9d79b" }}
                                  >
                                    {ar}
                                  </button>
                                );
                              })}
                            </div>
                          </div>

                          {/* Title Size picker */}
                          <div data-testid="thumbnail-size-picker">
                            <label className="flex items-center gap-1.5 text-xs font-bold text-white mb-1 px-0.5">
                              <Type size={13} style={{ color: "#5d8a52" }} /> Title Size
                            </label>
                            <div className="flex gap-1.5">
                              {[["sm", "SMALL"], ["md", "MEDIUM"], ["lg", "LARGE"], ["xl", "EXTRA LARGE"]].map(([key, label]) => (
                                <button
                                  key={key}
                                  type="button"
                                  data-testid={`thumbnail-size-${key}`}
                                  onClick={() => { setThumbSize(key); setThumbPreview(null); setThumbSaved(null); }}
                                  className="flex-1 rounded-xl px-1.5 py-2 text-[11px] sm:text-xs font-extrabold leading-tight text-center transition-colors"
                                  style={thumbSize === key
                                    ? { background: "#1e3aaa", color: "#fff", border: "2px solid #1e3aaa" }
                                    : { background: "#4f7746", color: "#fff", border: "2px solid #5d8a52" }}
                                >
                                  {label}
                                </button>
                              ))}
                            </div>
                            <p className="text-[11px] text-white mt-1 px-0.5">How big your title text appears — Small, Medium, Large, or Extra Large.</p>
                          </div>

                          {/* Website URL — baked crisply onto the bottom of the thumbnail */}
                          <div>
                            <label className="flex items-center gap-1.5 text-xs font-bold text-white mb-1 px-0.5">
                              <Link2 size={13} style={{ color: "#5d8a52" }} /> Add your website URL <span className="font-medium text-[#9a96ac]">(optional)</span>
                            </label>
                            <input
                              type="text"
                              data-testid="thumbnail-url-input"
                              value={websiteUrl}
                              onChange={(e) => { setWebsiteUrl(e.target.value); setThumbPreview(null); setThumbSaved(null); }}
                              maxLength={80}
                              placeholder="www.enteryourdomainhere.com"
                              className="w-full rounded-xl px-3 py-2.5 text-sm text-white bg-[#171622] placeholder:text-[#b7b3c6]"
                              style={{ border: "2px solid #5d8a52" }}
                            />
                            {websiteUrl.trim() && (
                              <div className="flex items-center gap-2 mt-2">
                                <span className="text-[11px] font-bold text-white">Text color:</span>
                                {[{ k: "white", sw: "#ffffff" }, { k: "black", sw: "#111111" }].map((c) => (
                                  <button
                                    key={c.k}
                                    type="button"
                                    data-testid={`url-color-${c.k}`}
                                    title={c.k}
                                    onClick={() => setUrlColor(c.k)}
                                    className="w-7 h-7 rounded-full transition-transform hover:scale-110"
                                    style={{ background: c.sw, border: urlColor === c.k ? "3px solid #5d8a52" : "2px solid #d8d2ea" }}
                                  />
                                ))}
                                <label
                                  data-testid="url-color-custom"
                                  title="Custom color"
                                  className="relative w-7 h-7 rounded-full cursor-pointer overflow-hidden flex items-center justify-center"
                                  style={{ background: urlCustomColor, border: urlColor === "custom" ? "3px solid #5d8a52" : "2px solid #d8d2ea" }}
                                >
                                  <Pencil size={11} style={{ color: "#fff", mixBlendMode: "difference" }} />
                                  <input type="color" value={urlCustomColor} onChange={(e) => { setUrlCustomColor(e.target.value); setUrlColor("custom"); }} className="absolute inset-0 opacity-0 cursor-pointer" />
                                </label>
                              </div>
                            )}
                            <p className="text-[11px] text-white mt-1 px-0.5">The domain that you enter will appear at the start and end of your video.</p>
                          </div>

                          {/* Use thumbnail as a 3-second intro title card */}
                          <label
                            data-testid="use-intro-toggle"
                            className="flex items-start gap-2.5 rounded-2xl border-2 px-3 py-2.5 cursor-pointer transition-colors"
                            style={{ borderColor: "#5d8a52", background: useIntro ? "rgba(143,191,130,.20)" : "#17171f" }}
                          >
                            <input
                              type="checkbox"
                              data-testid="use-intro-checkbox"
                              checked={useIntro}
                              onChange={(e) => setUseIntro(e.target.checked)}
                              className="mt-0.5 w-4 h-4 accent-[#4f7746] shrink-0"
                            />
                            <span className="text-xs leading-relaxed">
                              <span className="font-bold text-white">Use thumbnail as a 5-second intro</span>
                              <span className="block text-white">Adds your thumbnail as a short silent title card at the very start of the video. <span className="font-semibold">Free — no credits.</span> (Only added if you've made a thumbnail above.)</span>
                            </span>
                          </label>
                          {!thumbSaved && (
                            <button
                              type="button"
                              data-testid="generate-thumbnail-btn"
                              onClick={composeThumbnail}
                              disabled={thumbBusy || !thumbSourceId || !thumbTitle.trim()}
                              className={`w-full font-extrabold py-3 rounded-2xl flex items-center justify-center gap-2 disabled:opacity-60 transition-transform hover:scale-[1.01] ${thumbBusy ? "pe-render-fill text-black" : "text-white"}`}
                              style={thumbBusy ? { border: "2px solid #c79a05" } : { background: "#dc2626", border: "2px solid #b91c1c", boxShadow: "0 10px 22px -12px rgba(220,38,38,.85)" }}
                            >
                              {thumbBusy ? <><Loader2 size={17} className="spin" /> Building your preview…</> : <><Sparkles size={17} /> {thumbPreview ? "Update Preview" : "Create Thumbnail Preview"}</>}
                            </button>
                          )}
                          <input ref={uploadThumbRef} type="file" accept="image/*" className="hidden" data-testid="completion-upload-thumb-input" onChange={(e) => { uploadThumbFile(e.target.files?.[0]); e.target.value = ""; }} />
                          {cropSrc && <ThumbCropModal image={cropSrc} onCancel={() => setCropSrc(null)} onConfirm={applyCroppedThumb} />}
                          <button
                            type="button"
                            data-testid="completion-upload-thumb-btn"
                            onClick={() => uploadThumbRef.current?.click()}
                            disabled={thumbUploading}
                            className="w-full font-bold py-2.5 rounded-2xl flex items-center justify-center gap-2 disabled:opacity-60 transition-colors border-2 border-[#4f7746] text-white bg-[#171622] hover:bg-[#f2f7f0]"
                          >
                            {thumbUploading ? <><Loader2 size={16} className="spin" /> Uploading…</> : <><Upload size={16} /> Upload New Image</>}
                          </button>
                          {!thumbSourceId && !thumbSaved && (
                            <p className="text-[11px] text-white text-center -mt-1 px-2 leading-relaxed">
                              Pick a photo above and type your title to see a preview. It's <span className="font-bold text-[#4f7746]">free</span> — tweak and re-preview as often as you like.
                            </p>
                          )}
                          {/* Persistent full-size preview window — always visible; shows the logo before a preview exists */}
                          <div className="pt-1" data-testid="thumbnail-results">
                              <div className="rounded-2xl overflow-hidden border-2 border-[#5d8a52]">
                                <div className="aspect-video bg-[#eef3ea] relative flex items-center justify-center">
                                  {thumbPreview ? (
                                    <>
                                      <img src={imageUrl(thumbPreview)} alt="Thumbnail preview" className="w-full h-full object-cover" data-testid="thumbnail-preview-img" />
                                      {!thumbSaved && <span className="absolute top-2 left-2 text-[11px] font-bold text-white bg-black/55 rounded-full px-2 py-0.5">Preview</span>}
                                      {thumbSaved && <span className="absolute top-2 left-2 text-[11px] font-bold text-white bg-[#4f7746] rounded-full px-2 py-0.5 flex items-center gap-1"><Check size={12} /> Saved</span>}
                                    </>
                                  ) : (
                                    <div className="flex flex-col items-center justify-center gap-2 px-4 py-6 text-center" data-testid="thumbnail-preview-placeholder">
                                      <Logo size="sm" />
                                      <span className="text-sm font-extrabold text-white mt-1">Thumbnail Preview</span>
                                      <span className="text-[11px] text-[#9a96ac] leading-snug max-w-[260px]">Pick a photo above and type your title, then tap <b>Create Thumbnail Preview</b> — it'll appear right here.</span>
                                    </div>
                                  )}
                                </div>
                                {thumbPreview && (
                                  <div className="flex">
                                    {!thumbSaved ? (
                                      <button
                                        type="button"
                                        data-testid="save-thumbnail-btn"
                                        onClick={saveThumbnail}
                                        className="flex-1 font-extrabold py-2.5 flex items-center justify-center gap-2 text-white bg-[#4f7746] hover:bg-[#3f6238] transition-colors"
                                      >
                                        <Check size={16} /> Use Thumbnail
                                      </button>
                                    ) : (
                                      <button
                                        type="button"
                                        data-testid="download-thumbnail-btn"
                                        onClick={() => downloadThumbnail(thumbSaved)}
                                        className="flex-1 font-bold py-2.5 flex items-center justify-center gap-2 text-white bg-[#171622] hover:bg-[#faf8ff] hover:text-black transition-colors"
                                      >
                                        <Download size={16} /> Download
                                      </button>
                                    )}
                                  </div>
                                )}
                              </div>
                              {thumbSaved && (
                                <p className="text-[11px] text-[#4f7746] text-center font-semibold px-2 leading-relaxed mt-2" data-testid="thumbnail-saved-note">
                                  ✓ Locked in as your video's cover. Change anything above and tap "Create Thumbnail Preview" to redo it, then "Use Thumbnail" again. Now configure your video below.
                                </p>
                              )}
                          </div>
                        </div>
                    </div>
                </div>

              {/* CARD 4 — STEP EIGHT: Video configurator (no GUI change) */}
              <div className="rounded-2xl border-2 mt-4 p-5 sm:p-6 sms-fade-up" style={{ borderColor: "#5d8a52", background: "#0f0f16 repeating-linear-gradient(45deg, rgba(93,138,82,.06) 0px, rgba(93,138,82,.06) 10px, transparent 10px, transparent 20px)" }} data-testid="step-six-card">
              {/* STEP EIGHT header — full-width blue stripe across the top of the black card */}
              <div className="sms-step-head relative cursor-pointer select-none -mx-5 sm:-mx-6 -mt-5 sm:-mt-6 mb-4 py-2.5 text-center rounded-t-2xl" data-collapsed={!stepOpen("s8")} onClick={() => toggleStep("s8")} style={{ background: "#1e3aaa" }} data-testid="step-six-title-wrap">
                <StepTitle testid="step-six-title" left="STEP EIGHT" right="VOICE OVER" />
                <StepToggle k="s8" />
              </div>
              <div className="mt-3 mb-1 flex items-center justify-center gap-2 text-2xl font-extrabold text-white" data-testid="voice-over-studio-title">
                <Mic size={24} style={{ color: "#5d8a52" }} /> Voice Over Studio
              </div>

              <div className="mt-4">
                <div className="flex justify-center mb-2">
                  <button
                    type="button"
                    data-testid="opt-narration"
                    onClick={() => toggleOpt("narration")}
                    className="flex items-center justify-center gap-2 rounded-2xl border-2 px-5 py-2.5 transition-colors min-w-[240px]"
                    style={narration
                      ? { borderColor: "#e11d2a", background: "#e11d2a" }
                      : { borderColor: "#e11d2a", background: "#fff" }}
                  >
                    <Mic size={17} style={{ color: narration ? "#fff" : "#e11d2a" }} />
                    <span className="font-bold text-sm" style={{ color: narration ? "#fff" : "#171426" }}>Narration {narration ? "ON" : "OFF"}</span>
                  </button>
                </div>
                  <div className="rounded-2xl bg-black/40 border-2 border-[#5d8a52] p-2.5 mb-2">
                    <div className="grid grid-cols-2 gap-1.5 mb-2">
                      <button
                        type="button"
                        data-testid="voice-source-ai"
                        onClick={() => { setVoiceSource("ai"); if (videoState === "ready") setVideoState("idle"); }}
                        className="rounded-xl px-3 py-2 text-sm font-bold transition-colors flex items-center justify-center gap-1.5"
                        style={voiceSource === "ai" ? { background: "#1e3aaa", color: "#fff" } : { background: "#4f7746", color: "#fff", boxShadow: "inset 0 0 0 1.5px #5d8a52" }}
                      >
                        <Mic size={14} /> AI Voice
                      </button>
                      <button
                        type="button"
                        data-testid="voice-source-mine"
                        onClick={() => { setVoiceSource("mine"); if (videoState === "ready") setVideoState("idle"); }}
                        className="rounded-xl px-2 py-2 text-[13px] leading-tight font-bold transition-colors flex items-center justify-center gap-1.5 text-center"
                        style={voiceSource === "mine" ? { background: "#1e3aaa", color: "#fff" } : { background: "#4f7746", color: "#fff", boxShadow: "inset 0 0 0 1.5px #5d8a52" }}
                      >
                        <Mic size={14} className="shrink-0" /> Record Your Own Voice Over
                      </button>
                    </div>
                    {manual.diy && voiceSource === "mine" && (
                      <p className="text-[11px] text-[#a8a4ba] mb-2 px-0.5 leading-relaxed font-semibold">Record your own voice-over in the Recording Studio — it's yours, so there's no extra cost.</p>
                    )}
                    {voiceSource === "ai" ? (
                      <>
                        <div className="flex items-center gap-1.5 text-xs font-bold text-[#a8a4ba] mb-2 px-0.5">Narrator voice</div>
                        {elVoices.length > 0 ? (
                          <div className="space-y-2" data-testid="narration-voice-picker">
                            {[
                              { key: "cloned", label: "⭐ Your voice", list: elVoices.filter((v) => v.is_cloned) },
                              { key: "male", label: "Male", list: elVoices.filter((v) => !v.is_cloned && v.gender === "male").sort((a, b) => (a.source === "platform" ? 0 : 1) - (b.source === "platform" ? 0 : 1)) },
                              { key: "female", label: "Female", list: elVoices.filter((v) => !v.is_cloned && v.gender === "female").sort((a, b) => (a.source === "platform" ? 0 : 1) - (b.source === "platform" ? 0 : 1)) },
                            ].filter((g) => g.list.length > 0).map((g) => (
                              <div key={g.key}>
                                <div className="text-[10px] font-extrabold uppercase tracking-wide text-[#9a96ac] mb-1 px-0.5">{g.label}</div>
                                <div className="grid grid-cols-2 gap-1.5">
                                  {g.list.map((v) => {
                                    const active = narrationVoiceId === v.voice_id;
                                    return (
                                      <button
                                        key={v.voice_id}
                                        type="button"
                                        data-testid={`voice-opt-${v.voice_id}`}
                                        onClick={() => { setNarrationVoiceId(v.voice_id); if (videoState === "ready") setVideoState("idle"); }}
                                        className="relative text-left rounded-xl p-2 pr-8 transition-colors"
                                        style={active ? { background: "#1e3aaa", color: "#fff" } : { background: "#4f7746", boxShadow: "inset 0 0 0 1.5px #5d8a52" }}
                                      >
                                        <span className="block font-bold text-sm leading-tight" style={{ color: "#fff" }}>{v.name}</span>
                                        <span className="block text-[10.5px] leading-snug mt-0.5" style={{ color: active ? "rgba(255,255,255,.85)" : "rgba(255,255,255,.82)" }}>{v.desc}</span>
                                        <span
                                          role="button"
                                          tabIndex={0}
                                          data-testid={`voice-play-${v.voice_id}`}
                                          onClick={(e) => playSample(e, v)}
                                          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") playSample(e, v); }}
                                          title={`Hear ${v.name}`}
                                          className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full flex items-center justify-center cursor-pointer transition-colors"
                                          style={active ? { background: "rgba(255,255,255,.22)", color: "#fff" } : { background: "rgba(255,255,255,.20)", color: "#fff" }}
                                        >
                                          {playingVoiceId === v.voice_id ? <Loader2 size={12} className="animate-spin" /> : <Volume2 size={12} />}
                                        </span>
                                        {active && <Check size={13} className="absolute bottom-1.5 right-2 text-white" />}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            ))}
                            <p className="text-[11px] text-[#9a96ac] px-0.5 leading-relaxed">Studio-quality voices. Tap the speaker to hear a sample. Automatically falls back to a standard voice if unavailable.</p>
                          </div>
                        ) : (
                          <div className="grid grid-cols-2 gap-1.5">
                            <button type="button" data-testid="voice-female"
                              onClick={() => { setVoiceGender("female"); if (videoState === "ready") setVideoState("idle"); }}
                              className="rounded-xl px-3 py-2 text-sm font-bold transition-colors"
                              style={voiceGender === "female" ? { background: "#1e3aaa", color: "#fff" } : { background: "#4f7746", color: "#fff", boxShadow: "inset 0 0 0 1.5px #5d8a52" }}>Female</button>
                            <button type="button" data-testid="voice-male"
                              onClick={() => { setVoiceGender("male"); if (videoState === "ready") setVideoState("idle"); }}
                              className="rounded-xl px-3 py-2 text-sm font-bold transition-colors"
                              style={voiceGender === "male" ? { background: "#1e3aaa", color: "#fff" } : { background: "#4f7746", color: "#fff", boxShadow: "inset 0 0 0 1.5px #5d8a52" }}>Male</button>
                          </div>
                        )}
                        {voiceSource === "ai" && (
                          <div className="mt-2.5" data-testid="voice-pitch-picker">
                            <div className="flex items-center justify-between mb-1 px-0.5">
                              <span className="text-[11px] font-bold text-white">Voice age</span>
                              <button type="button" data-testid="voice-listen-btn" onClick={previewVoice} disabled={previewLoading}
                                className="flex items-center gap-1 text-[11px] font-bold rounded-full px-2.5 py-1 transition-colors disabled:opacity-60"
                                style={{ background: "#fff", color: "#4f7746", boxShadow: "inset 0 0 0 1.5px #cfe0c7" }}>
                                {previewLoading ? <Loader2 size={12} className="animate-spin" /> : <Volume2 size={12} />} Listen
                              </button>
                            </div>
                            <div className="grid grid-cols-3 gap-1.5">
                              {[["normal", "Normal"], ["higher", "Higher"], ["kid", "Kid"]].map(([key, label]) => (
                                <button key={key} type="button" data-testid={`voice-pitch-${key}`}
                                  onClick={() => { setVoicePitch(key); if (videoState === "ready") setVideoState("idle"); }}
                                  className="rounded-xl px-2 py-2 text-sm font-bold transition-colors"
                                  style={voicePitch === key ? { background: "#1e3aaa", color: "#fff" } : { background: "#4f7746", color: "#fff", boxShadow: "inset 0 0 0 1.5px #5d8a52" }}>
                                  {label}
                                </button>
                              ))}
                            </div>
                            <p className="text-[11px] text-[#9a96ac] mt-1 px-0.5">Raises the pitch for a younger voice — great for kids' stories. Tap Listen to hear it.</p>
                          </div>
                        )}
                      </>
                    ) : (
                      <div data-testid="inline-studio-wrap">
                        <p className="text-[11px] text-[#9a96ac] mb-2 px-0.5 leading-relaxed">Read the teleprompter or freestyle each step. Steps you skip play with music only.{recordedCount > 0 ? ` · ${recordedCount}/${(manual?.steps || []).length} recorded` : ""}</p>
                        <VoiceStudioModal
                          inline
                          manual={manual}
                          showLabels={user?.settings?.studio_labels !== false}
                          onClose={() => {}}
                          onSaved={() => api.get(`/manuals/${id}`).then(({ data }) => setManual(data)).catch(() => {})}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* STEP NINE — Music (window always open) */}
              <div className="rounded-2xl border-2 mt-4 p-5 sm:p-6 sms-fade-up" style={{ borderColor: "#5d8a52", background: "#0f0f16 repeating-linear-gradient(45deg, rgba(93,138,82,.06) 0px, rgba(93,138,82,.06) 10px, transparent 10px, transparent 20px)" }} data-testid="step-seven-card">
              <div className="sms-step-head relative cursor-pointer select-none -mx-5 sm:-mx-6 -mt-5 sm:-mt-6 mb-4 py-2.5 text-center rounded-t-2xl" data-collapsed={!stepOpen("s9")} onClick={() => toggleStep("s9")} style={{ background: "#1e3aaa" }}>
                <StepTitle left="STEP NINE" right="MUSIC" />
                <StepToggle k="s9" />
              </div>
              <div className="mt-4">
                <div className="flex justify-center mb-2">
                  <button
                    type="button"
                    data-testid="opt-music"
                    onClick={() => toggleOpt("music")}
                    className="flex items-center justify-center gap-2 rounded-2xl border-2 px-5 py-2.5 transition-colors min-w-[240px]"
                    style={music
                      ? { borderColor: "#e11d2a", background: "#e11d2a" }
                      : { borderColor: "#e11d2a", background: "#fff" }}
                  >
                    <Music size={17} style={{ color: music ? "#fff" : "#e11d2a" }} />
                    <span className="font-bold text-sm" style={{ color: music ? "#fff" : "#171426" }}>Music {music ? "ON" : "OFF"}</span>
                  </button>
                </div>
                  <div className="rounded-2xl bg-black/40 border-2 border-[#5d8a52] p-2.5 mb-2" data-testid="music-panel">
                    <div className="mb-2">
                        <div className="text-xs font-bold text-[#a8a4ba] mb-1.5 px-0.5">Your saved songs <span className="font-normal text-[#9a96ac]">({savedTracks.length}/5)</span></div>
                        <div className="flex flex-col gap-1.5">
                          {Array.from({ length: 5 }).map((_, i) => {
                            const t = savedTracks[i];
                            if (!t) {
                              return (
                                <button
                                  key={`empty-${i}`}
                                  type="button"
                                  data-testid={`saved-track-empty-${i}`}
                                  onClick={() => { setSaveSong(true); musicFileRef.current?.click(); }}
                                  className="flex items-center gap-2 rounded-xl px-3 py-2.5 border-2 border-dashed border-[#5d8a52] text-[#a9d79b] hover:bg-[#5d8a52]/15 transition-colors text-left"
                                >
                                  <Upload size={14} className="shrink-0" />
                                  <span className="text-sm font-semibold">Upload Song, Save it Here</span>
                                </button>
                              );
                            }
                            const active = selectedTrackId === t.id && !customMusic;
                            return (
                              <div
                                key={t.id}
                                data-testid={`saved-track-${t.id}`}
                                className="flex items-center justify-between gap-2 rounded-xl px-3 py-2 border transition-colors cursor-pointer"
                                style={active ? { background: "#1e3aaa", borderColor: "#18308c", color: "#fff" } : { background: "#4f7746", borderColor: "#3f6238", color: "#fff" }}
                                onClick={() => { setSelectedTrackId(t.id); setCustomMusic(null); if (videoState === "ready") setVideoState("idle"); }}
                              >
                                <span className="flex items-center gap-2 text-sm font-semibold truncate">
                                  <Music size={14} className="text-white shrink-0" />
                                  <span className="truncate">{t.name}</span>
                                </span>
                                <span className="flex items-center gap-2 shrink-0">
                                  {active && <Check size={16} />}
                                  <button
                                    data-testid={`saved-track-delete-${t.id}`}
                                    onClick={(e) => { e.stopPropagation(); deleteSavedTrack(t.id); }}
                                    title="Remove this song"
                                    className="text-white/75 hover:text-white"
                                  >
                                    <X size={15} />
                                  </button>
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                    <input ref={musicFileRef} type="file" accept=".mp3,audio/mpeg" className="hidden" onChange={pickMusicFile} data-testid="music-file-input" />
                    {customMusic ? (
                      <div className="bg-[#171622] rounded-xl px-3 py-2 border border-[#e7efe1]">
                        <div className="flex items-center justify-between gap-2">
                          <span className="flex items-center gap-2 text-sm font-semibold text-white truncate">
                            <Music size={14} className="text-[#5d8a52] shrink-0" /> <span className="truncate">{customMusic.name}</span>
                          </span>
                          <div className="flex items-center gap-1.5 shrink-0">
                            <button data-testid="music-file-preview" onClick={previewCustomMusic} title="Preview 5 seconds"
                              className="w-7 h-7 rounded-full flex items-center justify-center bg-[#eef3ea] text-[#5d8a52] hover:bg-[#e0ecd9] transition-colors">
                              {previewingMusic ? <Square size={13} /> : <Play size={13} />}
                            </button>
                            <button data-testid="music-file-remove" onClick={() => { setCustomMusic(null); setSaveSong(false); }} className="text-[#b7b3c8] hover:text-[#e11d48]"><X size={16} /></button>
                          </div>
                        </div>
                        {savedTracks.length < 5 ? (
                          <label className="flex items-center gap-2 mt-2 pt-2 border-t border-[#f0ecfb] cursor-pointer" data-testid="save-song-label">
                            <input type="checkbox" data-testid="save-song-checkbox" checked={saveSong} onChange={(e) => setSaveSong(e.target.checked)} className="w-4 h-4 accent-[#4f7746]" />
                            <span className="text-[13px] font-semibold text-[#4a475c]">Save Song? <span className="font-normal text-[#9a96ac]">Keep it on my account for future videos ({savedTracks.length}/5)</span></span>
                          </label>
                        ) : (
                          <p className="text-[11px] text-[#9a96ac] mt-2 pt-2 border-t border-[#f0ecfb]">You have 5 saved songs (the max). Delete one above to save this instead.</p>
                        )}
                      </div>
                    ) : (
                      <button
                        type="button"
                        data-testid="music-file-upload"
                        onClick={() => musicFileRef.current?.click()}
                        className="w-full flex items-center justify-center gap-2 bg-[#171622] rounded-xl px-3 py-2.5 border-2 border-dashed border-[#5d8a52] text-sm font-bold text-[#5d8a52] hover:bg-[#f6f3ff] transition-colors"
                      >
                        <Upload size={15} /> Upload an MP3
                      </button>
                    )}
                    <p className="text-xs text-[#9a96ac] mt-1.5 px-0.5">MP3 only. Keep it to ~1 minute — it loops automatically to fit your video's length.</p>
                    <p className="text-xs mt-2 px-0.5 font-semibold text-[#4f7746]" data-testid="music-youtube-tip">
                      🎵 Download music for free from the{" "}
                      <a href="https://studio.youtube.com/channel/UC/music" target="_blank" rel="noopener noreferrer" className="underline hover:text-[#3d5c37]">YouTube Studio Audio Library</a>
                      {" "}— they work great with Vittyyo!
                    </p>
                  </div>

                  {/* Audio mix — mirrors the Settings modal; both stay in unison */}
                  <div className="mt-3" data-testid="step-nine-audio-mix">
                    <div className="flex items-center gap-2 mb-1 px-0.5">
                      <Volume2 size={15} style={{ color: "#5d8a52" }} />
                      <span className="text-xs font-bold text-white uppercase tracking-wide">Audio Mix</span>
                      {mixSaving && <Loader2 size={13} className="spin text-[#a9d79b]" />}
                    </div>
                    <p className="text-[11px] text-[#9a96ac] mb-2 px-0.5 leading-snug">Set how loud each channel sits (1–10). These stay in sync with your Settings. Mute a channel to leave it out.</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                      {[
                        { key: "music_gain", muteKey: "music_muted", label: "Music", sub: "Background song" },
                        { key: "voice_gain", muteKey: "voice_muted", label: "Narration", sub: "Voice-over" },
                        { key: "intro_gain", label: "Intro Music", sub: "Opening swell" },
                        { key: "outro_gain", label: "Outro Music", sub: "Closing swell" },
                      ].map(({ key, muteKey, label, sub }) => {
                        const muted = muteKey ? !!mix[muteKey] : false;
                        const val = mix[key] ?? MIX_DEFS[key];
                        return (
                          <div key={key} className="flex items-center justify-between gap-2 rounded-2xl px-3 py-2.5" style={{ background: "#171622", border: "2px solid #5d8a52" }}>
                            <div className="flex items-center gap-2 min-w-0">
                              {muteKey ? (
                                <button data-testid={`mix-mute-${key}`} onClick={() => toggleMixMute(muteKey)} aria-label={`Mute ${label}`}
                                  className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-colors"
                                  style={muted ? { background: "#3a1620", color: "#f0637a" } : { background: "#16241a", color: "#a9d79b" }}>
                                  {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
                                </button>
                              ) : (
                                <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: "#16241a", color: "#a9d79b" }}>
                                  <Volume2 size={16} />
                                </span>
                              )}
                              <span className="leading-tight min-w-0">
                                <span className="block font-bold text-white text-sm truncate">{label}</span>
                                <span className="block text-[10px] text-[#9a96ac] truncate">{sub}</span>
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0" style={{ opacity: muted ? 0.4 : 1, pointerEvents: muted ? "none" : "auto" }}>
                              <button data-testid={`mix-${key}-minus`} onClick={() => bumpGain(key, -1)}
                                className="w-7 h-7 rounded-lg font-extrabold flex items-center justify-center hover:brightness-110 transition-all" style={{ background: "#16241a", color: "#a9d79b" }}>−</button>
                              <span data-testid={`mix-${key}-value`} className="w-6 text-center font-extrabold text-white">{muted ? "–" : val}</span>
                              <button data-testid={`mix-${key}-plus`} onClick={() => bumpGain(key, 1)}
                                className="w-7 h-7 rounded-lg font-extrabold flex items-center justify-center hover:brightness-110 transition-all" style={{ background: "#16241a", color: "#a9d79b" }}>+</button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>

              {/* STEP TEN — Text Overlay + language */}
              <div className="rounded-2xl border-2 mt-4 p-5 sm:p-6 sms-fade-up" style={{ borderColor: "#5d8a52", background: "#0f0f16 repeating-linear-gradient(45deg, rgba(93,138,82,.06) 0px, rgba(93,138,82,.06) 10px, transparent 10px, transparent 20px)" }} data-testid="step-eight-card">
              <div className="sms-step-head relative cursor-pointer select-none -mx-5 sm:-mx-6 -mt-5 sm:-mt-6 mb-4 py-2.5 text-center rounded-t-2xl" data-collapsed={!stepOpen("s10")} onClick={() => toggleStep("s10")} style={{ background: "#1e3aaa" }}>
                <StepTitle left="STEP TEN" right="TEXT & LANGUAGE" />
                <StepToggle k="s10" />
              </div>
              <div className="mt-4">
                <div className="flex justify-center mb-2">
                  <button
                    type="button"
                    data-testid="opt-text"
                    onClick={() => toggleOpt("text")}
                    className="flex items-center justify-center gap-2 rounded-2xl border-2 px-5 py-2.5 transition-colors min-w-[240px]"
                    style={textOverlay
                      ? { borderColor: "#e11d2a", background: "#e11d2a" }
                      : { borderColor: "#e11d2a", background: "#fff" }}
                  >
                    <Captions size={17} style={{ color: textOverlay ? "#fff" : "#e11d2a" }} />
                    <span className="font-bold text-sm" style={{ color: textOverlay ? "#fff" : "#171426" }}>Text Overlay {textOverlay ? "ON" : "OFF"}</span>
                  </button>
                </div>
                {textOverlay && (
                  <div className="mb-3" data-testid="overlay-title-row">
                    <label className="flex items-center gap-1.5 text-xs font-bold text-white mb-1 px-0.5">
                      <Captions size={13} style={{ color: "#5d8a52" }} /> On-screen title <span className="font-medium text-[#9a96ac]">— shown at the top of the video</span>
                    </label>
                    <textarea
                      data-testid="overlay-title-input"
                      value={overlayTitle}
                      onChange={(e) => setOverlayTitle(e.target.value.slice(0, 200))}
                      maxLength={200}
                      rows={2}
                      placeholder="e.g. Baby That 1957 Porsche 356 Speedster"
                      className="w-full rounded-xl px-3 py-2.5 text-sm text-white bg-[#171622] placeholder:text-[#b7b3c6] resize-none"
                      style={{ border: "2px solid #5d8a52" }}
                    />
                    <p className="text-[11px] text-[#a8a4ba] mt-1 px-0.5">
                      This exact text is <b>burned into the video</b> — shorten your long title here <b>before</b> you render. Leave blank to use the manual title.
                    </p>
                  </div>
                )}

                <div data-testid="video-language-picker" className="mb-2">
                  <div className="text-xs font-bold text-[#a8a4ba] mb-1.5 px-0.5">Language <span className="font-semibold text-[#9a96ac]">· voice-over + captions</span></div>
                  <div className="grid grid-cols-4 gap-1.5">
                    {Object.entries(LANGS).map(([code, { native }]) => {
                      const active = videoLang === code;
                      return (
                        <button
                          key={code}
                          type="button"
                          data-testid={`video-lang-${code}`}
                          onClick={() => {
                            setVideoLang(code);
                            try { localStorage.setItem("sms_video_lang", code); } catch { /* noop */ }
                          }}
                          className="rounded-xl px-2 py-2 text-sm font-bold transition-colors border-2 text-center"
                          style={active
                            ? { background: "#1e3aaa", borderColor: "#1e3aaa", color: "#fff" }
                            : { background: "#4f7746", borderColor: "#5d8a52", color: "#fff" }}
                        >
                          {native}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
              </div>

              {/* STEP ELEVEN — Amazon products to list (creator-curated, text only) */}
              <div className="rounded-2xl border-2 mt-4 p-5 sm:p-6 sms-fade-up" style={{ borderColor: "#5d8a52", background: "#0f0f16 repeating-linear-gradient(45deg, rgba(93,138,82,.06) 0px, rgba(93,138,82,.06) 10px, transparent 10px, transparent 20px)" }} data-testid="step-nine-card">
                <div className="sms-step-head relative cursor-pointer select-none -mx-5 sm:-mx-6 -mt-5 sm:-mt-6 mb-4 py-2.5 text-center rounded-t-2xl" data-collapsed={!stepOpen("s11")} onClick={() => toggleStep("s11")} style={{ background: "#1e3aaa" }}>
                  <StepTitle left="STEP ELEVEN" right="AMAZON PRODUCTS" />
                  <StepToggle k="s11" />
                </div>
                <div className="flex items-center justify-center gap-2 mb-1">
                  <ShoppingCart size={18} className="text-[#5d8a52]" />
                  <h3 className="font-extrabold text-lg text-white">List Amazon Products</h3>
                </div>
                <p className="text-sm text-[#a8a4ba] text-center mb-4">
                  We found products related to your video. Tick <b className="text-white">"List it"</b> on the ones you want shown on your public video page — you earn from qualifying Amazon purchases. Nothing is listed until you tick it.
                </p>

                {candidates === null ? (
                  <div className="py-10 flex flex-col items-center gap-3 text-[#a8a4ba]" data-testid="candidates-loading">
                    <Loader2 size={24} className="spin text-[#5d8a52]" />
                    <p className="text-sm font-medium">Finding relevant products…</p>
                  </div>
                ) : candidates.length === 0 ? (
                  <div className="py-8 text-center" data-testid="candidates-empty">
                    <p className="text-sm text-[#a8a4ba]">{candErr ? "Couldn't load product suggestions right now." : "No obvious products to list for this video — that's okay, some topics don't need any."}</p>
                    <button onClick={regenerateCandidates} data-testid="candidates-regenerate" className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-[#4f7746] border-2 border-[#5d8a52] rounded-full px-4 py-2 hover:bg-[#eef3ea] transition-colors">
                      <RotateCcw size={14} /> Try again
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between mb-2 px-0.5">
                      <span className="text-xs font-bold text-[#a8a4ba]">{candidates.filter((c) => c.approved).length} of {candidates.length} listed</span>
                      <button onClick={regenerateCandidates} data-testid="candidates-regenerate" className="inline-flex items-center gap-1 text-xs font-bold text-[#9a96ac] hover:text-[#4f7746] transition-colors">
                        <RotateCcw size={12} /> Refresh list
                      </button>
                    </div>
                    <div className="flex justify-center mb-2.5">
                      <button
                        type="button"
                        data-testid="candidates-select-all"
                        onClick={() => setAllCandidates(!(candidates.length > 0 && candidates.every((c) => c.approved)))}
                        className="inline-flex items-center gap-2 text-sm font-extrabold text-white bg-[#1e3aaa] hover:bg-[#16308c] px-5 py-2 rounded-full transition-colors shadow-sm"
                      >
                        <ListChecks size={16} />
                        {candidates.length > 0 && candidates.every((c) => c.approved) ? "Deselect All" : "Select All"}
                      </button>
                    </div>
                    <div className="space-y-2">
                      {candidates.map((c) => (
                        <label
                          key={c.id}
                          data-testid={`candidate-${c.id}`}
                          className="flex items-start gap-3 rounded-2xl border-2 p-3 cursor-pointer transition-colors bg-[#0f0f16] text-white"
                          style={{ borderColor: c.approved ? "#1e3aaa" : "#5d8a52" }}
                        >
                          <button
                            type="button"
                            role="checkbox"
                            aria-checked={c.approved}
                            data-testid={`candidate-toggle-${c.id}`}
                            onClick={(e) => { e.preventDefault(); toggleCandidate(c.id, !c.approved); }}
                            className="mt-0.5 w-6 h-6 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors"
                            style={c.approved ? { background: "#1e3aaa", borderColor: "#1e3aaa" } : { background: "transparent", borderColor: "#9a96ac" }}
                          >
                            {c.approved && <Check size={15} className="text-white" />}
                          </button>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              {c.category ? <span className="text-[11px] font-bold tracking-wide uppercase text-[#9a96ac]">{c.category}</span> : <span />}
                              {c.price_range && <span className="text-xs font-bold text-white bg-[#1e3aaa] px-2.5 py-0.5 rounded-full whitespace-nowrap shrink-0">{c.price_range}</span>}
                            </div>
                            <div className="font-extrabold text-[15px] leading-snug text-white mt-0.5">{c.product_name}</div>
                            {c.description && <p className="text-[13px] text-[#a8a4ba] mt-0.5 leading-snug line-clamp-2">{c.description}</p>}
                            <span className={`inline-flex items-center gap-1 text-[11px] font-bold mt-1 ${c.approved ? "text-[#1e3aaa]" : "text-[#9a96ac]"}`}>
                              {c.approved ? <><Check size={12} /> Listed on your video page</> : "Tap to list it"}
                            </span>
                          </div>
                        </label>
                      ))}
                    </div>
                    <p className="text-xs text-[#b7b3c8] mt-3 text-center" data-testid="candidates-disclosure">As an Amazon Associate, Vittyyo earns from qualifying purchases. Prices are approximate.</p>
                  </>
                )}
              </div>


              {/* STEP TWELVE — Retention Card configurator */}
              <div className="rounded-2xl border-2 mt-4 p-5 sm:p-6 sms-fade-up" style={{ borderColor: "#5d8a52", background: "#0f0f16 repeating-linear-gradient(45deg, rgba(93,138,82,.06) 0px, rgba(93,138,82,.06) 10px, transparent 10px, transparent 20px)" }} data-testid="step-ten-retention-card">
                <div className="sms-step-head relative cursor-pointer select-none -mx-5 sm:-mx-6 -mt-5 sm:-mt-6 mb-4 py-2.5 text-center rounded-t-2xl" data-collapsed={!stepOpen("s12")} onClick={() => toggleStep("s12")} style={{ background: "#1e3aaa" }} data-testid="step-ten-retention-title-wrap">
                  <StepTitle left="STEP TWELVE" right="RETENTION CARD" />
                  <StepToggle k="s12" />
                </div>
                <p className="text-sm text-[#a8a4ba] text-center mb-4">Fun Fact Card <span className="text-[#9a96ac]">· keep viewers watching</span></p>
                <RetentionCardStudio
                  manualId={id}
                  orientation={orientation}
                  initial={manual?.retention}
                />
                <RiddleCardStudio
                  manualId={id}
                  orientation={orientation}
                  initial={manual?.riddle}
                />
              </div>


              {/* STEP THIRTEEN — Intro Video Clip */}
              <div className="rounded-2xl border-2 mt-4 p-5 sm:p-6 sms-fade-up" style={{ borderColor: "#5d8a52", background: "#0f0f16 repeating-linear-gradient(45deg, rgba(93,138,82,.06) 0px, rgba(93,138,82,.06) 10px, transparent 10px, transparent 20px)" }} data-testid="step-eleven-introclip">
                <div className="sms-step-head relative cursor-pointer select-none -mx-5 sm:-mx-6 -mt-5 sm:-mt-6 mb-4 py-2.5 text-center rounded-t-2xl" data-collapsed={!stepOpen("s13")} onClick={() => toggleStep("s13")} style={{ background: "#1e3aaa" }} data-testid="step-eleven-introclip-title-wrap">
                  <StepTitle left="STEP THIRTEEN" right="VIDEO CLIPS" />
                  <StepToggle k="s13" />
                </div>
                <p className="text-sm text-[#a8a4ba] text-center mb-4">Intro, Mid-Roll &amp; Outro Video Clips <span className="text-[#9a96ac]">· optional cinematic hooks</span></p>
                <IntroClipStudio
                  manualId={id}
                  slot="intro"
                  initial={manual?.intro_clip}
                  title="Opening Clip"
                  hint="Plays right after your opening title card, before the voiceover begins — a great cinematic hook (made in your own tool, e.g. Kling)."
                />
                <div className="h-px my-6" style={{ background: "#2a2536" }} />
                <IntroClipStudio
                  manualId={id}
                  slot="mid"
                  initial={manual?.mid_clip}
                  title="Mid-Roll Clip"
                  hint="Drops in around the 3-minute mark, between two steps — a mid-video breather or hook to re-grab attention (made in your own tool, e.g. Kling). Only lands on videos longer than 3 minutes."
                />
                <div className="h-px my-6" style={{ background: "#2a2536" }} />
                <IntroClipStudio
                  manualId={id}
                  slot="outro"
                  initial={manual?.outro_clip}
                  title="Closing Clip"
                  hint="Plays at the very end, right before your End Card — a slick sign-off (e.g. a Corvette into the sunset)."
                />
              </div>


              {/* STEP FOURTEEN — Video Quiz (opt-in end-of-video comprehension quiz) */}
              <div className="rounded-2xl border-2 mt-4 p-5 sm:p-6 sms-fade-up" style={{ borderColor: "#5d8a52", background: "#0f0f16 repeating-linear-gradient(45deg, rgba(93,138,82,.06) 0px, rgba(93,138,82,.06) 10px, transparent 10px, transparent 20px)" }} data-testid="step-video-quiz">
                <div className="sms-step-head relative cursor-pointer select-none -mx-5 sm:-mx-6 -mt-5 sm:-mt-6 mb-4 py-2.5 text-center rounded-t-2xl" data-collapsed={!stepOpen("s14")} onClick={() => toggleStep("s14")} style={{ background: "#1e3aaa" }} data-testid="step-video-quiz-title-wrap">
                  <StepTitle left="STEP FOURTEEN" right="VIDEO QUIZ" />
                  <StepToggle k="s14" />
                </div>
                <p className="text-sm text-[#a8a4ba] text-center mb-4">Video Quiz <span className="text-[#9a96ac]">· test your comprehension</span></p>
                <VideoQuizStudio
                  manualId={id}
                  initial={manual?.video_quiz}
                />
              </div>


              {/* STEP FIFTEEN — Custom End Card configurator */}
              <div className="rounded-2xl border-2 mt-4 p-5 sm:p-6 sms-fade-up" style={{ borderColor: "#5d8a52", background: "#0f0f16 repeating-linear-gradient(45deg, rgba(93,138,82,.06) 0px, rgba(93,138,82,.06) 10px, transparent 10px, transparent 20px)" }} data-testid="step-eleven-endcard">
                <div className="sms-step-head relative cursor-pointer select-none -mx-5 sm:-mx-6 -mt-5 sm:-mt-6 mb-4 py-2.5 text-center rounded-t-2xl" data-collapsed={!stepOpen("s15")} onClick={() => toggleStep("s15")} style={{ background: "#1e3aaa" }} data-testid="step-eleven-endcard-title-wrap">
                  <StepTitle left="STEP FIFTEEN" right="END CARD" />
                  <StepToggle k="s15" />
                </div>
                <p className="text-sm text-[#a8a4ba] text-center mb-4">Custom End Card <span className="text-[#9a96ac]">· your logo, QR &amp; call-to-action</span></p>
                <EndCardStudio
                  manualId={id}
                  orientation={orientation}
                  initial={manual?.endcard}
                />
              </div>


              {/* STEP SIXTEEN — Finalize and Render */}
              <div className="rounded-2xl border-2 mt-4 p-6 sm:p-8 sms-fade-up" style={{ borderColor: "#5d8a52", background: "#0f0f16 repeating-linear-gradient(45deg, rgba(93,138,82,.06) 0px, rgba(93,138,82,.06) 10px, transparent 10px, transparent 20px)" }} data-testid="create-video-card">
              <div className="sms-step-head relative cursor-pointer select-none -mx-6 sm:-mx-8 -mt-6 sm:-mt-8 mb-5 py-2.5 text-center rounded-t-2xl" data-collapsed={!stepOpen("s16")} onClick={() => toggleStep("s16")} style={{ background: "#1e3aaa" }} data-testid="step-ten-title-wrap">
                <StepTitle testid="step-ten-title" left="STEP SIXTEEN" right="FINALIZE & RENDER" />
                <StepToggle k="s16" />
              </div>
              <p className="text-sm text-[#a8a4ba] text-center mb-5">Finalize and Render</p>
              <div className="mt-1">
                <div data-testid="aspect-ratio-picker" className="mb-2">
                  <div className="text-xs font-bold text-[#a8a4ba] mb-1.5 px-0.5">Aspect ratio</div>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { v: "landscape", Icon: RectangleHorizontal, label: "Widescreen", sub: "16:9 · YouTube · TV" },
                      { v: "portrait", Icon: RectangleVertical, label: "Portrait", sub: "9:16 · Reels · Shorts" },
                    ].map(({ v, Icon, label, sub, soon }) => {
                      const active = orientation === v;
                      return (
                        <button
                          key={v}
                          type="button"
                          data-testid={`aspect-${v}`}
                          disabled={soon}
                          onClick={() => {
                            if (soon) return;
                            setOrientation(v);
                            try { localStorage.setItem("sms_video_orientation", v); } catch { /* noop */ }
                            if (videoState === "ready") setVideoState("idle");
                          }}
                          className={`rounded-xl px-3 py-2.5 flex items-center justify-center gap-2 transition-colors border-2 ${soon ? "opacity-45 cursor-not-allowed" : ""}`}
                          style={active
                            ? { background: "#1e3aaa", borderColor: "#1e3aaa", color: "#fff" }
                            : { background: "#4f7746", borderColor: "#5d8a52", color: "#fff" }}
                        >
                          <Icon size={20} style={{ color: "#fff" }} />
                          <div className="text-left leading-tight">
                            <div className="font-bold text-sm">{label}</div>
                            <div className="text-[10px]" style={{ opacity: 0.85 }}>{sub}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
                {(() => {
                  const curKey = Object.keys(videoVariants).find((k) => {
                    const p = parseVariantKey(k);
                    return p.lang === videoLang && p.orient === orientation;
                  });
                  if (curKey) {
                    return (
                      <div className="space-y-2">
                        <button
                          data-testid="download-video-btn"
                          onClick={() => downloadVideo(curKey)}
                          className="w-full font-bold py-3.5 rounded-2xl flex items-center justify-center gap-2 border-2 text-white bg-[#1e3aaa] border-[#1e3aaa] hover:bg-[#182f88] transition-colors"
                        >
                          <Download size={18} /> Video Complete! — Click to Download
                        </button>
                        <p className="flex items-center justify-center gap-1.5 text-[11px] font-semibold text-[#4f7746] text-center leading-snug" data-testid="saved-to-library-note">
                          <Check size={13} className="shrink-0" /> Saved to your <b>My&nbsp;Video&nbsp;Archive</b> — you can safely leave this page and reopen it anytime.
                        </p>
                        {user?.unlimited && (
                          <>
                            <button
                              data-testid="rerender-video-btn"
                              onClick={() => createVideo()}
                              className="w-full font-bold py-3 rounded-2xl flex items-center justify-center gap-2 border-2 text-white bg-[#7c5cff] border-[#7c5cff] hover:bg-[#6d4bff] transition-colors"
                            >
                              <RotateCcw size={16} /> Re-Render Video (Apply any new changes)
                            </button>
                            <p className="text-[11px] text-[#9a96ac] text-center">Admin only · re-renders are free · overwrites this version (make your thumbnail first to add the intro)</p>
                          </>
                        )}
                      </div>
                    );
                  }
                  return (
                    (() => {
                      const nativeLang = (manual.language || "en").toLowerCase();
                      const isLangCopy = !!manual.lang_copy_target;
                      if (!isLangCopy && videoLang !== nativeLang) {
                        // A different language than this video's own → make a SEPARATE copy (keeps the original intact).
                        return (
                          <button
                            data-testid="make-language-copy-btn"
                            onClick={() => makeLanguageCopy(videoLang)}
                            disabled={langCopyBusy}
                            className="w-full font-bold py-3.5 rounded-2xl flex items-center justify-center gap-2 transition-colors border-2 border-[#1e3aaa] text-white bg-[#1e3aaa] hover:bg-[#182f88] disabled:opacity-60"
                          >
                            {langCopyBusy ? <Loader2 size={18} className="spin" /> : <Languages size={18} />}
                            Make {LANGS[videoLang]?.label || videoLang.toUpperCase()} copy
                          </button>
                        );
                      }
                      return (
                        <button
                          data-testid="create-video-btn"
                          onClick={() => createVideo()}
                          className="w-full font-bold py-3.5 rounded-2xl flex items-center justify-center gap-2 transition-colors border-2 border-[#dc2626] text-white bg-[#dc2626] hover:bg-[#b91c1c]"
                        >
                          <Video size={18} /> Create {videoLang !== "en" ? `${LANGS[videoLang]?.label} ` : ""}Video
                          {manual.free_sample && !manual.video_credited && (
                            <span data-testid="free-video-pill" className="text-[10px] font-extrabold uppercase tracking-wide text-white bg-[#4f7746] px-2 py-0.5 rounded-full">Free</span>
                          )}
                        </button>
                      );
                    })()
                  );
                })()}
                {manual.free_sample && !manual.video_credited && (
                  <p data-testid="free-video-note" className="text-xs text-[#5d8a52] font-semibold text-center mt-2">
                    🎬 Your free manual includes a free video — bring it to life!
                  </p>
                )}
                {!user?.unlimited && !(manual.free_sample && !manual.video_credited) && (() => {
                  const nativeLang = (manual.language || "en").toLowerCase();
                  const isLangCopy = !!manual.lang_copy_target;
                  const renderedLangs = new Set(Object.keys(videoVariants).map((k) => k.split(":")[0]));
                  let hint;
                  if (!isLangCopy && videoLang !== nativeLang) hint = `Makes a separate ${LANGS[videoLang]?.label || videoLang.toUpperCase()} copy in your Videos — you'll render it there ($0.50 extra language).`;
                  else if (!manual.video_credited) hint = manual.diy ? "Building was free — your Creator video is $2.99, charged only when it's ready." : "One flat $3.99 — everything included, charged only when your video is ready.";
                  else if (!renderedLangs.has(videoLang)) hint = `Extra language — $0.50, charged only when it's ready.`;
                  else hint = "Free — you've already made this language (re-renders are free).";
                  return (
                    <p data-testid="video-cost-hint" className="text-xs text-[#9a96ac] text-center mt-2">
                      {hint}
                    </p>
                  );
                })()}
              </div>
              </div>
              </>
            )}
          </div>

          {/* Reassuring "you're done" lightbox — fires once on a successful render. */}
          {showDoneModal && (
            <div
              className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
              data-testid="video-done-modal"
              onClick={() => setShowDoneModal(false)}
            >
              <div
                className="bg-[#1e3aaa] rounded-3xl w-full max-w-sm p-7 text-center shadow-2xl border-2 border-[#4a5fc5]"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="w-14 h-14 mx-auto rounded-full bg-white/15 flex items-center justify-center mb-3">
                  <Check size={30} className="text-white" strokeWidth={3} />
                </div>
                <h3 className="text-white font-extrabold text-2xl leading-tight">Your Video is Completed!</h3>
                <p className="text-white/90 text-sm mt-2 leading-relaxed">
                  View it anytime on your <b>My&nbsp;Videos</b> page.
                </p>
                <button
                  data-testid="video-done-thanks-btn"
                  onClick={() => { setShowDoneModal(false); navigate("/my-videos"); }}
                  className="mt-5 w-full bg-[#0f0f16] text-[#ffd21e] font-extrabold py-3 rounded-full hover:bg-[#191927] transition-colors"
                >
                  Take Me To My Video
                </button>
                {manual.mode !== "minute" && (
                  <button
                    data-testid="make-social-cut-btn"
                    onClick={() => { setShowDoneModal(false); setSocialOpen(true); }}
                    className="mt-2.5 w-full inline-flex items-center justify-center gap-2 bg-[#0f0f16] text-white font-extrabold py-3 rounded-full hover:bg-[#191927] transition-colors"
                  >
                    <Film size={18} /> Create 60-sec Social Video · $1.99
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Social Cut upsell — turn this finished guide into a 60s vertical reel (reuses its photos) */}
          {socialOpen && (
            <div
              className="fixed inset-0 z-[75] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
              data-testid="social-cut-modal"
              onClick={() => !socialCreating && setSocialOpen(false)}
            >
              <div className="sms-tweed bg-[#0f0f16] rounded-3xl w-full max-w-md p-6 shadow-2xl border-2 border-[#1e3aaa]" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center gap-2 mb-1">
                  <Film size={20} className="text-[#5b9bff]" />
                  <h3 className="text-white font-extrabold text-xl leading-tight">Turn this into a scroll-stopping Reel</h3>
                </div>
                <p className="text-white/70 text-[13px] mb-4 leading-relaxed">
                  We'll reuse this video's photos and let AI pick the <b>12 most eye-catching shots</b> (5s each) for a punchy 60-second vertical reel. Add a marketing hook below — then you'll finalize voice &amp; music (or record your own) and confirm the one-time <b className="text-white">$1.99</b>.
                </p>

                <label className="block text-[11px] font-bold text-[#a8a4ba] uppercase tracking-wide mb-1">On-screen hook</label>
                <input
                  data-testid="social-hook-input"
                  value={socialHook}
                  onChange={(e) => setSocialHook(e.target.value)}
                  maxLength={80}
                  placeholder="e.g. 3 secrets to restaurant pasta at home 🍝"
                  className="w-full rounded-xl px-4 py-3 text-sm text-white bg-black placeholder:text-white/35 outline-none mb-3"
                  style={{ border: "2px solid #1e3aaa" }}
                />
                <label className="block text-[11px] font-bold text-[#a8a4ba] uppercase tracking-wide mb-1">Caption / call-to-action <span className="normal-case text-[#7c7791]">(optional)</span></label>
                <textarea
                  data-testid="social-caption-input"
                  value={socialCaption}
                  onChange={(e) => setSocialCaption(e.target.value)}
                  maxLength={140}
                  rows={2}
                  placeholder="Full recipe on Vittyyo — link in bio!"
                  className="w-full rounded-xl px-4 py-3 text-sm text-white bg-black placeholder:text-white/35 outline-none resize-none leading-snug mb-3"
                  style={{ border: "2px solid #1e3aaa" }}
                />

                <button
                  data-testid="social-write-for-me-btn"
                  onClick={async () => {
                    setSocialDrafting(true);
                    try {
                      const { data } = await api.post(`/manuals/${id}/social-cut/draft`);
                      if (data?.hook) setSocialHook(data.hook);
                      if (data?.caption) setSocialCaption(data.caption);
                      toast.success("Drafted a hook for you ✨ — tweak it if you like");
                    } catch { toast.error("Couldn't draft copy right now — type your own or try again"); }
                    finally { setSocialDrafting(false); }
                  }}
                  disabled={socialDrafting || socialCreating}
                  className="w-full inline-flex items-center justify-center gap-2 text-sm font-bold text-[#5b9bff] border-2 border-[#1e3aaa] rounded-xl py-2.5 mb-4 disabled:opacity-60"
                >
                  {socialDrafting ? <><Loader2 size={15} className="animate-spin" /> Writing…</> : <><Sparkles size={15} /> Write it for me</>}
                </button>

                <div className="flex items-center gap-2">
                  <button
                    data-testid="social-create-btn"
                    onClick={async () => {
                      setSocialCreating(true);
                      try {
                        const { data } = await api.post(`/manuals/${id}/social-cut`, { hook: socialHook.trim(), caption: socialCaption.trim() });
                        if (data?.id) { toast.success("Reel built — finalize voice & music, then publish"); navigate(`/minute/${data.id}`); }
                      } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't build the reel — please try again"); }
                      finally { setSocialCreating(false); }
                    }}
                    disabled={socialCreating}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-[#1e3aaa] text-white font-extrabold py-3 rounded-xl hover:bg-[#183089] transition-colors disabled:opacity-60"
                  >
                    {socialCreating ? <><Loader2 size={16} className="animate-spin" /> Building your reel…</> : <><Film size={16} /> Build my Reel</>}
                  </button>
                  <button
                    data-testid="social-cancel-btn"
                    onClick={() => setSocialOpen(false)}
                    disabled={socialCreating}
                    className="px-4 py-3 rounded-xl font-bold text-white/70 hover:text-white disabled:opacity-60"
                  >
                    Not now
                  </button>
                </div>
              </div>
            </div>
          )}

      {/* Disclaimer / category modal */}
      {/* Hero full-size lightbox */}
      {previewImg && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
          onClick={() => { setPreviewImg(null); }}
          data-testid="step-preview-overlay"
        >
          <button
            data-testid="step-preview-close"
            onClick={() => { setPreviewImg(null); }}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center transition-colors"
            aria-label="Close"
          >
            <X size={20} />
          </button>
          <div className="flex flex-col items-center gap-3 w-full max-w-[760px] max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="text-white/80 text-sm font-bold">Step {previewImg.index + 1}</div>
            <div className="relative inline-block rounded-xl">
              <img
                src={imageUrl(previewImg.id)}
                alt={`Step ${previewImg.index + 1}`}
                className={`max-w-full max-h-[62vh] w-auto h-auto object-contain rounded-xl sms-fade-up ${regenSet[previewImg.index] ? "opacity-40" : ""}`}
              />
              {(() => {
                const photos = (manual.steps || []).map((s, idx) => ({ id: s.thumbnail_id, index: idx })).filter((p) => p.id);
                if (photos.length < 2) return null;
                const pos = photos.findIndex((p) => p.index === previewImg.index);
                const go = (dir, e) => { e.stopPropagation(); const n = (pos + dir + photos.length) % photos.length; setPreviewImg(photos[n]); setFixText(""); setFixZoom(""); };
                return (
                  <>
                    <button data-testid="step-preview-prev" onClick={(e) => go(-1, e)} className="absolute left-2 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center transition-colors z-10 shadow-lg" aria-label="Previous image"><ChevronLeft size={26} /></button>
                    <button data-testid="step-preview-next" onClick={(e) => go(1, e)} className="absolute right-2 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center transition-colors z-10 shadow-lg" aria-label="Next image"><ChevronRight size={26} /></button>
                  </>
                );
              })()}
              {regenSet[previewImg.index] && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white">
                  <Loader2 size={30} className="spin" />
                  <span className="text-xs font-semibold">Applying your fix…</span>
                </div>
              )}
            </div>
            {/* Punch-in cut-ins are baked in automatically now — no manual picker. */}

            <div className="sms-tweed w-full bg-[#171622] rounded-2xl border-2 border-[#5d8a52] p-3 shadow-lg" style={{ display: manual.diy ? "none" : undefined }}>
              {(() => {
                const idx = previewImg.index;
                const step = manual.steps?.[idx] || {};
                const verdict = scanResults[idx];
                const isGood = verdict && verdict.ok;
                const needsConfirm = isGood && !insistFix;
                const limit = manual.redo_limit ?? 2;
                // Creator Mode: 2 free redos per VIDEO, then $0.25 each (never blocked).
                const diyFreeLeft = manual.diy ? Math.max(0, 2 - (manual.redos_used || 0)) : 0;
                const left = manual.diy ? 99 : Math.max(0, limit - (step.redo_count ?? (step.redone ? 1 : 0)));
                const isRedone = !!step.redone && !!step.original_thumbnail_id;
                const activeIsOriginal = step.original_thumbnail_id && step.thumbnail_id === step.original_thumbnail_id;
                const busy = !!regenSet[idx];
                const doRedo = () => { if (needsConfirm) { setInsistFix(true); return; } regenerateStep(idx, fixText.trim(), fixZoom); };
                const doGeneric = () => { if (needsConfirm) { setInsistFix(true); return; } regenerateStep(idx, null, fixZoom); };
                const downloadThisImage = () => {
                  const imgId = step.thumbnail_id || previewImg.id;
                  if (!imgId) return;
                  const fname = `stepmystep-${(manual.title || "photo").replace(/[^a-z0-9]+/gi, "-").toLowerCase().slice(0, 50)}-step-${idx + 1}.jpg`;
                  const a = document.createElement("a");
                  a.href = `${imageUrl(imgId)}?dl=1&name=${encodeURIComponent(fname)}`;
                  a.download = fname;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                };
                const downloadBtnFull = (
                  <button
                    data-testid="step-download-image"
                    onClick={downloadThisImage}
                    title="Download this photo to animate it in your own tool, then bring the clip back in at Step Thirteen"
                    className="w-full mt-2 flex items-center justify-center gap-2 text-sm font-extrabold text-white bg-[#1e6fff] rounded-xl px-4 py-2.5 hover:bg-[#155fe0] transition-colors"
                  >
                    <Download size={16} /> Download this Image
                  </button>
                );

                const redoToggle = isRedone && (
                    <div data-testid="redo-toggle" className="mb-3">
                      <div className="flex items-center gap-2 text-xs font-bold text-[#15803d] bg-[#eafaef] rounded-lg px-3 py-2 mb-2.5">
                        <ShieldCheck size={15} className="shrink-0" />
                        <span>Your original photo is saved — pick whichever you like best.</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          data-testid="use-original-btn"
                          onClick={() => toggleStepImage(idx, "original")}
                          className={`flex items-center justify-center gap-2 text-sm font-extrabold rounded-xl px-3 py-2.5 border-2 transition-colors ${activeIsOriginal ? "bg-[#4f7746] text-white border-[#4f7746]" : "bg-[#171622] text-[#4f7746] border-[#5d8a52] hover:bg-[#eef3ea]"}`}
                        >
                          {activeIsOriginal && <Check size={16} />} Original
                        </button>
                        <button
                          data-testid="use-new-btn"
                          onClick={() => toggleStepImage(idx, "redo")}
                          className={`flex items-center justify-center gap-2 text-sm font-extrabold rounded-xl px-3 py-2.5 border-2 transition-colors ${!activeIsOriginal ? "bg-[#4f7746] text-white border-[#4f7746]" : "bg-[#171622] text-[#4f7746] border-[#5d8a52] hover:bg-[#eef3ea]"}`}
                        >
                          {!activeIsOriginal && <Check size={16} />} New version
                        </button>
                      </div>
                      <p className="text-[11px] text-[#9a96ac] mt-2 leading-snug">
                        Switching between the two is free.
                      </p>
                    </div>
                );

                // No redos left on this photo → blocked, original is safe.
                if (left <= 0) {
                  return (
                    <div data-testid="no-redos-left">
                      {redoToggle}
                      <div className="flex items-start gap-2 text-xs font-bold text-[#b45309] bg-[#fff7e6] rounded-lg px-3 py-2">
                        <AlertTriangle size={15} className="shrink-0 mt-0.5" />
                        <span>You've used both redos on this photo. Your original is safe — pick Original or New above.</span>
                      </div>
                      {downloadBtnFull}
                    </div>
                  );
                }

                // Redos available → the fix flow.
                return (
                  <>
                    {redoToggle}
                    {verdict && (
                      <div
                        data-testid="lightbox-verdict"
                        className={`flex items-start gap-2 text-xs font-bold rounded-lg px-3 py-2 mb-2 ${verdict.ok ? "bg-[#eafaef] text-[#15803d]" : "bg-[#fff7e6] text-[#b45309]"}`}
                      >
                        {verdict.ok ? <ShieldCheck size={15} className="shrink-0 mt-0.5" /> : <AlertTriangle size={15} className="shrink-0 mt-0.5" />}
                        <span>{verdict.ok ? "This photo passed the check — no obvious problems found." : verdict.reason}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-[11px] font-bold text-[#a8a4ba] uppercase tracking-wide">Tell us what to fix</label>
                      <span data-testid="redos-left-pill" className="text-[10px] font-extrabold text-[#4f7746] bg-[#eef3ea] rounded-full px-2 py-0.5">{manual.diy ? (diyFreeLeft > 0 ? `${diyFreeLeft} free redo${diyFreeLeft === 1 ? "" : "s"} left` : "$0.25 per redo") : left === 1 ? "One redo left" : `${left} redos left`}</span>
                    </div>
                    <p className="text-[11px] text-[#9a96ac] mb-1 leading-snug" data-testid="step-fix-subtext">
                      Enter what you want to fix in the image — be specific about what you'd like to see.
                    </p>
                    <textarea
                      data-testid="step-fix-input"
                      value={fixText}
                      onChange={(e) => setFixText(e.target.value)}
                      rows={3}
                      maxLength={900}
                      placeholder="e.g. move the man into the driver's seat behind the wheel, smiling, hands on the wheel — keep the same garage and lighting"
                      className="sms-input w-full px-3 py-2 text-sm resize-none mt-1"
                      style={{ border: "2px solid #5d8a52" }}
                    />
                    <div className="mt-2" data-testid="step-fix-zoom-wrap">
                      <label className="text-[11px] font-bold text-[#a8a4ba] uppercase tracking-wide">Zoom image</label>
                      <select
                        data-testid="step-fix-zoom"
                        value={fixZoom}
                        onChange={(e) => setFixZoom(e.target.value)}
                        className="w-full mt-1 rounded-xl px-3 py-2.5 text-sm font-bold text-[#2a2536] bg-white outline-none"
                        style={{ border: "2px solid #5d8a52" }}
                      >
                        <option value="">Keep current framing</option>
                        <option value="tight">Tight shot — full bleed (emblems, engines, products)</option>
                        <option value="70">Zoom in · 70%</option>
                        <option value="60">Zoom in · 60%</option>
                        <option value="50">Zoom in · 50%</option>
                        <option value="40">Zoom in · 40%</option>
                        <option value="wide">Wide shot — pull back (drone / establishing)</option>
                      </select>
                      <p className="text-[10px] text-[#9a96ac] mt-1 leading-snug">Reframes the shot when you redo — great when images feel too wide, or to pull back for an environmental establishing look.</p>
                    </div>
                    {isGood && insistFix && (
                      <p className="text-[12px] font-semibold text-[#b45309] mt-2" data-testid="softlock-note">
                        This photo already looks fine — tap again to redo it anyway (uses one of your redos).
                      </p>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        data-testid="step-fix-redo"
                        onClick={doRedo}
                        disabled={busy || (!fixText.trim() && !fixZoom)}
                        className="flex-1 flex items-center justify-center gap-2 text-sm font-extrabold text-white bg-[#4f7746] rounded-xl px-4 py-2.5 hover:bg-[#3f6238] transition-colors disabled:opacity-50"
                      >
                        {busy ? <Loader2 size={16} className="spin" /> : <RotateCcw size={16} />} {needsConfirm ? "Fix anyway" : "Redo with my fix"}
                      </button>
                      <button
                        data-testid="step-download-image"
                        onClick={downloadThisImage}
                        title="Download this photo to animate it in your own tool, then bring the clip back in at Step Thirteen"
                        className="flex-1 flex items-center justify-center gap-2 text-sm font-extrabold text-white bg-[#1e6fff] rounded-xl px-4 py-2.5 hover:bg-[#155fe0] transition-colors"
                      >
                        <Download size={16} /> Download this Image
                      </button>
                    </div>
                    <button
                      data-testid="step-fix-generic"
                      onClick={doGeneric}
                      disabled={busy}
                      title="Just clean up glitches, keep the same scene"
                      className="w-full mt-2 text-xs font-bold text-[#4f7746] bg-[#eef3ea] border-2 border-[#5d8a52] rounded-xl px-3 py-2.5 hover:bg-[#dfead8] transition-colors disabled:opacity-50"
                    >
                      Just fix glitches
                    </button>
                    <p className="text-[11px] text-[#9a96ac] mt-1.5 leading-snug">
                      We keep the same person, background and setting — and change only what you describe. Your original is saved so you can flip back anytime.
                    </p>
                  </>
                );
              })()}

              {/* Replace with the creator's OWN uploaded photo — free, no AI, no redo used.
                  Great for service businesses that want real client/before-after shots in the video. */}
              {(() => {
                const idx = previewImg.index;
                const step = manual.steps?.[idx] || {};
                const busy = !!regenSet[idx];
                const uploaded = !!step.uploaded_photo;
                return (
                  <div className="mt-4 pt-3 border-t border-[#eef0ec]" data-testid="upload-photo-block">
                    <label className="text-[11px] font-bold text-[#a8a4ba] uppercase tracking-wide">Or use your own photo</label>
                    <p className="text-[11px] text-[#9a96ac] mt-0.5 mb-2 leading-snug">
                      Free — no redo used. Upload a real photo to replace this shot (your original stays saved).
                    </p>
                    <div className="flex items-center gap-3">
                      <label
                        data-testid="upload-photo-btn"
                        className={`flex-1 flex items-center justify-center gap-2 text-sm font-extrabold rounded-xl px-4 py-2.5 border-2 transition-colors cursor-pointer ${busy ? "opacity-50 pointer-events-none" : ""} text-white bg-[#4f7746] border-[#5d8a52] hover:bg-[#3f6238]`}
                      >
                        {busy ? <Loader2 size={16} className="spin" /> : <Upload size={16} />}
                        {uploaded ? "Replace Image" : "Upload a Photo"}
                        <input
                          data-testid="upload-photo-input"
                          type="file"
                          accept="image/*"
                          className="hidden"
                          disabled={busy}
                          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) uploadStepPhoto(idx, f); }}
                        />
                      </label>
                      {uploaded && step.thumbnail_id && (
                        <div className="shrink-0 flex items-center gap-1.5" data-testid="upload-photo-confirm">
                          <div className="relative w-12 h-12 rounded-lg overflow-hidden border-2 border-[#4f7746]">
                            <img src={imageUrl(step.thumbnail_id)} alt="Your uploaded photo" className="w-full h-full object-cover" />
                          </div>
                          <Check size={16} className="text-[#4f7746]" />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Escape hatch — reuse any other step's photo (free, no generation, no redo used).
                  Great when a photo has an unfixable glitch or you're out of redos. */}
              {(() => {
                const idx = previewImg.index;
                const others = (manual.steps || [])
                  .map((s, i) => ({ i, id: s.thumbnail_id }))
                  .filter((x) => x.i !== idx && x.id);
                if (!others.length) return null;
                return (
                  <div className="mt-4 pt-3 border-t border-[#eef0ec]" data-testid="reuse-photo-strip">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <label className="text-[11px] font-bold text-[#a8a4ba] uppercase tracking-wide">Or swap in another step's photo</label>
                      <button
                        data-testid="flip-photo-btn"
                        onClick={() => flipPhoto(idx)}
                        disabled={!!regenSet[idx]}
                        title="Mirror this photo horizontally (free, no redo used)"
                        className="flex items-center gap-1.5 shrink-0 font-bold text-[#4f7746] bg-[#171622] border-2 border-[#5d8a52] rounded-lg px-2.5 py-1 text-xs hover:bg-[#eef3ea] transition-colors disabled:opacity-50"
                      >
                        <FlipHorizontal size={14} /> Flip
                      </button>
                    </div>
                    <p className="text-[11px] text-[#9a96ac] mb-2 leading-snug">Free — no redo used. Tap one to replace this photo (your original stays saved).</p>
                    <div className="flex gap-2 overflow-x-auto pb-1">
                      {others.map(({ i, id }) => (
                        <button
                          key={i}
                          data-testid={`reuse-photo-${i}`}
                          onClick={() => reusePhoto(idx, i)}
                          disabled={!!regenSet[idx]}
                          title={`Use Step ${i + 1}'s photo`}
                          className="relative shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 border-[#5d8a52] hover:ring-2 hover:ring-[#4f7746] transition-all disabled:opacity-50"
                        >
                          <img src={imageUrl(id)} alt={`Step ${i + 1}`} className="w-full h-full object-cover" />
                          <span className="absolute bottom-0 inset-x-0 bg-black/55 text-white text-[9px] font-bold text-center py-0.5">Step {i + 1}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {heroZoom && (manual.completion_image_id || manual.hero_image_id) && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
          onClick={() => setHeroZoom(false)}
          data-testid="hero-zoom-overlay"
        >
          <button
            data-testid="hero-zoom-close"
            onClick={() => setHeroZoom(false)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center transition-colors"
            aria-label="Close"
          >
            <X size={20} />
          </button>
          <img
            src={imageUrl(manual.completion_image_id || manual.hero_image_id)}
            alt={manual.title || "Your vision"}
            onClick={(e) => e.stopPropagation()}
            className="max-w-full max-h-[90vh] object-contain rounded-xl sms-fade-up"
          />
        </div>
      )}

      <PrintableManual manual={manual} />
    </div>
  );
}
